import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import api, { trackActivity } from '../services/api';
import ResearchReportModal from '../components/ResearchReportModal';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Scatter } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import './FundScreenerApp.css';
import ManagedDisclaimer from '../components/ManagedDisclaimer';

// Register ChartJS components including zoom plugin
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  zoomPlugin
);

// Color palette for multiple fund lines
const CHART_COLORS = [
  '#072040', '#f0a500', '#599BE3', '#91CF9B', '#E74C3C',
  '#9B59B6', '#1ABC9C', '#E67E22', '#34495E', '#7F8C8D'
];


// Numeric filter column definitions
const NUMERIC_FILTER_COLUMNS = [
  { key: 'fee', label: 'TER', pct: true },
  { key: 'return_1m', label: '1m Return', pct: true },
  { key: 'return_3m', label: '3m Return', pct: true },
  { key: 'return_6m', label: '6m Return', pct: true },
  { key: 'return_12m', label: '12m Return', pct: true },
  { key: 'return_2yr', label: '2yr Return', pct: true },
  { key: 'return_3yr', label: '3yr Return', pct: true },
  { key: 'return_5yr', label: '5yr Return', pct: true },
  { key: 'return_7yr', label: '7yr Return', pct: true },
  { key: 'return_10yr', label: '10yr Return', pct: true },
  { key: 'vol_3yr', label: '3yr Vol', pct: true },
  { key: 'intercept', label: 'Intercept' },
  { key: 'max_drawdown', label: 'Max Drawdown', pct: true },
  { key: 'tracking_error', label: 'Tracking Error', pct: true },
  { key: 'min_alpha', label: 'Min Alpha', pct: true },
  { key: 'max_alpha', label: 'Max Alpha', pct: true },
  { key: 'std_dev_alpha', label: 'Std Dev Alpha', pct: true },
  { key: 'alpha_streak', label: 'Streak' },
  { key: 'pctile_3yr', label: 'Percentile', pct: true },
  { key: 'alpha_slope_12m', label: 'Trend Slope' },
];

const OPERATOR_OPTIONS = [
  { key: 'gte', label: '\u2265', title: 'Greater than or equal' },
  { key: 'lte', label: '\u2264', title: 'Less than or equal' },
  { key: 'gt', label: '>', title: 'Greater than' },
  { key: 'lt', label: '<', title: 'Less than' },
];

const FundScreenerApp = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const isDrummondUser = user?.is_drummond_user;
  // Factor-analysis content is internal Drummond IP — branded/external users see a trimmed list.
  const numericFilterColumns = isDrummondUser
    ? NUMERIC_FILTER_COLUMNS
    : NUMERIC_FILTER_COLUMNS.filter(c => c.key !== 'intercept');
  // State
  const [openReportModals, setOpenReportModals] = useState([]);
  const [filterOptions, setFilterOptions] = useState(null);
  const [filters, setFilters] = useState({
    asset_class: [],
    style: [],
    tilt: [],
    hedged: [],
    held: [],
  });
  const [flagshipOnly, setFlagshipOnly] = useState(true);
  const [funds, setFunds] = useState([]);
  const [refreshDate, setRefreshDate] = useState(null);
  // Universe-wide time machine: null = "Latest" (existing /funds path); a YYYY-MM-DD month-end
  // routes the whole grid through /funds-asat, restating every fund at that date.
  const [asAtDate, setAsAtDate] = useState(null);
  const [asAtDates, setAsAtDates] = useState([]);
  const [selectedFunds, setSelectedFunds] = useState([]);
  const [hideUnselected, setHideUnselected] = useState(false);
  const [chartData, setChartData] = useState(null);
  const [showAverage, setShowAverage] = useState(false);
  const [averageChartData, setAverageChartData] = useState(null);
  const [isLoadingAverage, setIsLoadingAverage] = useState(false);
  const [benchmarks, setBenchmarks] = useState([]);
  const [isLoadingFilters, setIsLoadingFilters] = useState(true);
  const [isLoadingFunds, setIsLoadingFunds] = useState(false);
  const [isLoadingCharts, setIsLoadingCharts] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'fund_name', direction: 'asc' });
  const [searchText, setSearchText] = useState('');
  const [numericFilters, setNumericFilters] = useState([]);
  const [filterPopup, setFilterPopup] = useState(null); // column key string or null
  const [alphaModalFund, setAlphaModalFund] = useState(null); // Fund to show in alpha modal
  const [isLoadingAlpha, setIsLoadingAlpha] = useState(false);
  // Returns modal state
  const [returnsModalFund, setReturnsModalFund] = useState(null);
  const [returnsModalData, setReturnsModalData] = useState(null);
  const [returnsAvailableDates, setReturnsAvailableDates] = useState([]);
  const [returnsSelectedDate, setReturnsSelectedDate] = useState('');
  const [isLoadingReturns, setIsLoadingReturns] = useState(false);

  // Freeze-pane: measure thead height so we can apply sticky top offsets to cells
  const theadRef = useRef(null);
  const [frozenOffsets, setFrozenOffsets] = useState({ header: 0, benchmarks: [], average: 0 });

  const measureFrozenOffsets = useCallback(() => {
    const thead = theadRef.current;
    if (!thead) return;
    const rows = thead.querySelectorAll('tr');
    if (!rows.length) return;
    const headerH = rows[0].getBoundingClientRect().height;
    let cumulative = headerH;
    const bmOffsets = [];
    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      if (row.classList.contains('benchmark-row')) {
        bmOffsets.push(cumulative);
        cumulative += row.getBoundingClientRect().height;
      } else if (row.classList.contains('average-row')) {
        break;
      }
    }
    setFrozenOffsets({ header: 0, benchmarks: bmOffsets, average: cumulative });
  }, []);

  // Re-measure when benchmark/average data changes or table remounts after loading
  useEffect(() => {
    measureFrozenOffsets();
    // Also measure after a short delay to catch late renders
    const timer = setTimeout(measureFrozenOffsets, 100);
    return () => clearTimeout(timer);
  }, [benchmarks.length, measureFrozenOffsets, isLoadingFunds, funds.length]);

  // ResizeObserver as fallback for layout shifts (re-attach when table remounts after loading)
  useEffect(() => {
    const thead = theadRef.current;
    if (!thead) return;
    const ro = new ResizeObserver(measureFrozenOffsets);
    ro.observe(thead);
    return () => ro.disconnect();
  }, [measureFrozenOffsets, isLoadingFunds]);

  // Fullscreen chart modal state
  const [fullscreenChart, setFullscreenChart] = useState(null); // { type, title, data, options }
  const fullscreenChartRef = useRef(null);

  // Manager heatmap and rankings modal state
  const [heatmapModalOpen, setHeatmapModalOpen] = useState(false);
  const [heatmapData, setHeatmapData] = useState(null);
  const [isLoadingHeatmap, setIsLoadingHeatmap] = useState(false);
  const [heatmapSelectedAssetClasses, setHeatmapSelectedAssetClasses] = useState([]);
  // Alpha Regime (peer comparison) modal state
  const [alphaRegimeData, setAlphaRegimeData] = useState(null);
  const [isLoadingAlphaRegime, setIsLoadingAlphaRegime] = useState(false);
  const [alphaRegimeModalOpen, setAlphaRegimeModalOpen] = useState(false);

  // Factor methodology modal
  const [factorMethodOpen, setFactorMethodOpen] = useState(false);


  // Factor loading state
  const [factorData, setFactorData] = useState(null);
  const [isLoadingFactors, setIsLoadingFactors] = useState(false);
  const [factorWeights, setFactorWeights] = useState({});
  const [factorModalData, setFactorModalData] = useState(null);
  const [isLoadingFactorModal, setIsLoadingFactorModal] = useState(false);
  const [factorModalOpen, setFactorModalOpen] = useState(false);

  // Style/Tilt Returns modal state
  const [styleReturnsModalOpen, setStyleReturnsModalOpen] = useState(false);
  const [styleReturnsData, setStyleReturnsData] = useState(null);
  const [isLoadingStyleReturns, setIsLoadingStyleReturns] = useState(false);
  const [styleReturnsAssetClass, setStyleReturnsAssetClass] = useState(null);
  const [styleReturnsSelectedStyles, setStyleReturnsSelectedStyles] = useState([]);
  const [styleReturnsGroupBy, setStyleReturnsGroupBy] = useState('style');

  // Track app access and load filter options on mount
  useEffect(() => {
    trackActivity('app_access', 'fund_screener');
    loadFilterOptions();
    loadAsAtDates();
  }, []);



  // Reload filter options when asset class or style changes (contextual filters)
  useEffect(() => {
    loadFilterOptions(
      filters.asset_class.length > 0 ? filters.asset_class : null,
      filters.style.length > 0 ? filters.style : null
    );
  }, [filters.asset_class, filters.style]);

  // Load funds and benchmarks when filters change
  useEffect(() => {
    loadFunds();
    const hasFilters = Object.values(filters).some(v => v.length > 0);
    const trCodes = selectedFunds.map(f => f.tr_code).filter(Boolean);
    if (hasFilters || trCodes.length > 0) {
      loadBenchmarks(filters, trCodes);
    } else {
      setBenchmarks([]);
    }
  }, [filters, flagshipOnly, asAtDate]);

  // Load benchmarks for selected funds (when selection changes, or the as-at date changes)
  useEffect(() => {
    const hasFilters = Object.values(filters).some(v => v.length > 0);
    const trCodes = selectedFunds.map(f => f.tr_code).filter(Boolean);
    if (trCodes.length > 0) {
      loadBenchmarks(filters, trCodes);
    } else if (!hasFilters) {
      setBenchmarks([]);
    }
  }, [selectedFunds, asAtDate]);

  // Load chart data and factor data when selected funds change
  useEffect(() => {
    if (selectedFunds.length > 0) {
      loadChartData();
      if (isDrummondUser) {
        loadFactorData();
      }
    } else {
      setChartData(null);
      setFactorData(null);
      setFactorWeights({});
    }
  }, [selectedFunds]);

  const loadFilterOptions = async (assetClasses = null, styles = null) => {
    try {
      const params = new URLSearchParams();
      if (assetClasses && assetClasses.length > 0) {
        assetClasses.forEach(ac => params.append('asset_class', ac));
      }
      if (styles && styles.length > 0) {
        styles.forEach(s => params.append('style', s));
      }
      const response = await api.get(`/api/screener/filters?${params.toString()}`);
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error loading filter options:', error);
    } finally {
      setIsLoadingFilters(false);
    }
  };

  const loadAsAtDates = async () => {
    try {
      const response = await api.get('/api/screener/asat-dates');
      setAsAtDates(response.data.dates || []);
    } catch (error) {
      console.error('Error loading as-at dates:', error);
    }
  };

  const loadBenchmarks = async (activeFilters, trCodes = []) => {
    try {
      const params = new URLSearchParams();
      Object.entries(activeFilters).forEach(([key, values]) => {
        if (values.length > 0) {
          values.forEach(v => params.append(key, v));
        }
      });
      trCodes.forEach(code => params.append('tr_codes', code));
      // Historical mode: recompute benchmark rows at the selected month-end
      if (asAtDate) params.append('date', asAtDate);

      const paramStr = params.toString();
      if (!paramStr) return;

      const response = await api.get(`/api/screener/benchmarks?${paramStr}`);
      setBenchmarks(response.data.benchmarks || []);
    } catch (error) {
      console.error('Error loading benchmarks:', error);
      setBenchmarks([]);
    }
  };

  const loadFunds = async () => {
    setIsLoadingFunds(true);
    try {
      const params = new URLSearchParams();

      Object.entries(filters).forEach(([key, values]) => {
        if (values.length > 0) {
          values.forEach(v => params.append(key, v));
        }
      });

      if (flagshipOnly) {
        params.append('flagship', '1');
      }

      // Latest => existing /funds (unchanged). A selected month-end => /funds-asat restates
      // the whole universe at that date.
      const endpoint = asAtDate
        ? `/api/screener/funds-asat?date=${encodeURIComponent(asAtDate)}&${params.toString()}`
        : `/api/screener/funds?${params.toString()}`;

      const response = await api.get(endpoint);
      setFunds(response.data.funds || []);
      // Keep refreshDate intact in historical mode so returning to Latest restores it
      if (!asAtDate) setRefreshDate(response.data.refresh_date || null);
    } catch (error) {
      console.error('Error loading funds:', error);
    } finally {
      setIsLoadingFunds(false);
    }
  };

  const loadChartData = async () => {
    setIsLoadingCharts(true);
    try {
      const trCodes = selectedFunds.map(f => f.tr_code).filter(Boolean);
      if (trCodes.length === 0) {
        setChartData(null);
        return;
      }

      const params = new URLSearchParams();
      trCodes.forEach(code => params.append('tr_codes', code));

      const response = await api.get(`/api/screener/charts?${params.toString()}`);
      setChartData(response.data);
    } catch (error) {
      console.error('Error loading chart data:', error);
    } finally {
      setIsLoadingCharts(false);
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => {
      const current = prev[filterName];
      const newValues = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [filterName]: newValues };
    });
  };

  const clearFilter = (filterName) => {
    setFilters(prev => ({ ...prev, [filterName]: [] }));
  };

  const clearAllFilters = () => {
    setFilters({
      asset_class: [],
      style: [],
      tilt: [],
      hedged: [],
      held: [],
    });
  };

  const handleAsAtChange = (e) => {
    setAsAtDate(e.target.value || null);
  };

  const toggleFundSelection = (fund) => {
    setSelectedFunds(prev => {
      const isSelected = prev.some(f => f.tr_code === fund.tr_code);
      if (isSelected) {
        return prev.filter(f => f.tr_code !== fund.tr_code);
      } else {
        return [...prev, fund];
      }
    });
  };

  const clearSelection = () => {
    setSelectedFunds([]);
    setHideUnselected(false);
  };

  // Fetch alpha data for a single fund on demand
  const loadAlphaForFund = async (fund) => {
    if (!fund.tr_code) return;

    setIsLoadingAlpha(true);
    try {
      const params = new URLSearchParams();
      params.append('tr_codes', fund.tr_code);

      const response = await api.get(`/api/screener/charts?${params.toString()}`);
      const alphaData = response.data.cumulative_alpha?.[0];

      if (alphaData) {
        setAlphaModalFund(alphaData);
      }
    } catch (error) {
      console.error('Error loading alpha data:', error);
    } finally {
      setIsLoadingAlpha(false);
    }
  };

  const handleViewReport = (fund, e) => {
    e.stopPropagation(); // Prevent row selection
    setOpenReportModals(prev => prev.includes(fund.apir_code) ? prev : [...prev, fund.apir_code]);
  };

  // Open returns modal and load available dates
  const openReturnsModal = async (fund, e) => {
    e.stopPropagation();
    if (!fund.tr_code) return;

    setReturnsModalFund(fund);
    setReturnsModalData(null);
    setReturnsSelectedDate('');
    setIsLoadingReturns(true);

    try {
      const response = await api.get(`/api/screener/fund-returns?tr_code=${fund.tr_code}`);
      setReturnsAvailableDates(response.data.available_dates || []);
      // Auto-select first (most recent) date
      if (response.data.available_dates?.length > 0) {
        const firstDate = response.data.available_dates[0];
        setReturnsSelectedDate(firstDate);
        loadReturnsForDate(fund.tr_code, firstDate);
      }
    } catch (error) {
      console.error('Error loading returns dates:', error);
    } finally {
      setIsLoadingReturns(false);
    }
  };

  // Load returns data for a specific date
  const loadReturnsForDate = async (trCode, date) => {
    setIsLoadingReturns(true);
    try {
      const response = await api.get(`/api/screener/fund-returns?tr_code=${trCode}&date=${date}`);
      setReturnsModalData(response.data);
    } catch (error) {
      console.error('Error loading returns data:', error);
    } finally {
      setIsLoadingReturns(false);
    }
  };

  // Handle date change in returns modal
  const handleReturnsDateChange = (e) => {
    const newDate = e.target.value;
    setReturnsSelectedDate(newDate);
    if (returnsModalFund?.tr_code && newDate) {
      loadReturnsForDate(returnsModalFund.tr_code, newDate);
    }
  };

  // Close returns modal
  const closeReturnsModal = () => {
    setReturnsModalFund(null);
    setReturnsModalData(null);
    setReturnsAvailableDates([]);
    setReturnsSelectedDate('');
  };

  // Load manager heatmap data
  const loadHeatmapData = async () => {
    setHeatmapModalOpen(true);
    setIsLoadingHeatmap(true);
    try {
      const response = await api.get('/api/screener/manager-heatmap');
      setHeatmapData(response.data.heatmap || []);
    } catch (error) {
      console.error('Error loading heatmap data:', error);
      setHeatmapData([]);
    } finally {
      setIsLoadingHeatmap(false);
    }
  };

  // Close heatmap modal
  const closeHeatmapModal = () => {
    setHeatmapModalOpen(false);
    setHeatmapData(null);
    setHeatmapSelectedAssetClasses([]);
  };

  // Style Returns modal
  const loadStyleReturns = async (assetClass, groupBy = styleReturnsGroupBy) => {
    setStyleReturnsAssetClass(assetClass);
    setIsLoadingStyleReturns(true);
    try {
      const response = await api.get(`/api/screener/style-returns?asset_class=${encodeURIComponent(assetClass)}&group_by=${groupBy}`);
      setStyleReturnsData(response.data.groups || []);
      setStyleReturnsSelectedStyles([]);
    } catch (error) {
      console.error('Error loading style returns:', error);
      setStyleReturnsData([]);
    } finally {
      setIsLoadingStyleReturns(false);
    }
  };

  const openStyleReturnsModal = () => {
    setStyleReturnsModalOpen(true);
    setStyleReturnsData(null);
    setStyleReturnsAssetClass(null);
    setStyleReturnsSelectedStyles([]);
    setStyleReturnsGroupBy('style');
  };

  const closeStyleReturnsModal = () => {
    setStyleReturnsModalOpen(false);
    setStyleReturnsData(null);
    setStyleReturnsAssetClass(null);
    setStyleReturnsSelectedStyles([]);
    setStyleReturnsGroupBy('style');
  };

  const handleGroupByChange = (groupBy) => {
    setStyleReturnsGroupBy(groupBy);
    setStyleReturnsSelectedStyles([]);
    if (styleReturnsAssetClass) {
      loadStyleReturns(styleReturnsAssetClass, groupBy);
    }
  };

  const toggleStyleReturnsStyle = (style) => {
    setStyleReturnsSelectedStyles(prev =>
      prev.includes(style)
        ? prev.filter(s => s !== style)
        : [...prev, style]
    );
  };

  // Toggle asset class filter for heatmap
  const toggleHeatmapAssetClass = (assetClass) => {
    setHeatmapSelectedAssetClasses(prev =>
      prev.includes(assetClass)
        ? prev.filter(ac => ac !== assetClass)
        : [...prev, assetClass]
    );
  };

  // Get unique asset classes from heatmap data
  const heatmapAssetClasses = useMemo(() => {
    if (!heatmapData) return [];
    return [...new Set(heatmapData.map(row => row.asset_class))].sort();
  }, [heatmapData]);

  // Heatmap rows to exclude (asset_class | grouping_type | style)
  const HEATMAP_EXCLUSIONS = new Set([
    "AREIT|Style|Real Assets",
    "AREIT|Tilt|Benchmark",
    "AREIT|Tilt|Passive",
    "Australian Equities|Style|SMID",
    "Australian Equities|Tilt|Benchmark",
    "Australian Equities|Tilt|Growth",
    "Australian Equities|Tilt|Impact",
    "Australian Equities|Tilt|Negative Screening",
    "Australian Equities|Tilt|Neutral",
    "Australian Equities|Tilt|Passive",
    "Australian Equities|Tilt|Positive Screening",
    "Australian Equities|Tilt|Value",
    "Commodities|Style|Defensive",
    "Commodities|Style|Growth",
    "Commodities|Tilt|Passive",
    "Commodities|Tilt|Sustainable",
    "EM Equities|Style|Regional",
    "EM Equities|Style|Sustainable",
    "EM Equities|Tilt|Benchmark",
    "EM Equities|Tilt|Quant",
    "GREIT|Tilt|Passive",
    "Global Alpha|Style|Defensive",
    "Global Alpha|Style|Growth",
    "Infrastructure|Tilt|Benchmark",
    "Infrastructure|Tilt|Passive",
    "International Equities|Style|Regional",
    "International Equities|Style|Sectoral",
    "International Equities|Tilt|Benchmark",
    "International Equities|Tilt|Growth",
    "International Equities|Tilt|Impact",
    "International Equities|Tilt|Negative Screening",
    "International Equities|Tilt|Neutral",
    "International Equities|Tilt|Passive",
    "International Equities|Tilt|Positive Screening",
    "International Equities|Tilt|Value",
    "Private Debt|Style|Australian",
    "Private Debt|Style|Global",
    "Private Debt|Tilt|Floating",
    "Private Equity|Style|Australian",
    "Private Equity|Style|Global",
    "Private Equity|Tilt|Hybrid",
    "Private Equity|Tilt|Listed",
    "Private Equity|Tilt|Multi-Asset",
    "Private Equity|Tilt|Unlisted",
  ]);

  // Filter heatmap data based on selected asset classes (show nothing until selection made)
  const filteredHeatmapData = useMemo(() => {
    if (!heatmapData) return [];
    if (heatmapSelectedAssetClasses.length === 0) return [];
    return heatmapData.filter(row =>
      heatmapSelectedAssetClasses.includes(row.asset_class) &&
      !HEATMAP_EXCLUSIONS.has(`${row.asset_class}|${row.grouping_type}|${row.style}`)
    );
  }, [heatmapData, heatmapSelectedAssetClasses]);

  // Load alpha regime (peer comparison) data for a fund
  const loadAlphaRegime = async (fund, e) => {
    if (e) e.stopPropagation();
    if (!fund.tr_code) return;
    setAlphaRegimeModalOpen(true);
    setIsLoadingAlphaRegime(true);
    try {
      const response = await api.get(`/api/screener/alpha-regime?tr_code=${fund.tr_code}`);
      setAlphaRegimeData(response.data);
    } catch (error) {
      console.error('Error loading alpha regime data:', error);
      setAlphaRegimeData(null);
    } finally {
      setIsLoadingAlphaRegime(false);
    }
  };

  const closeAlphaRegimeModal = () => {
    setAlphaRegimeModalOpen(false);
    setAlphaRegimeData(null);
  };


  // Load factor loading data for all selected funds
  const loadFactorData = async () => {
    if (!isDrummondUser) return;
    const trCodes = selectedFunds.map(f => f.tr_code).filter(Boolean);
    if (trCodes.length === 0) {
      setFactorData(null);
      return;
    }
    setIsLoadingFactors(true);
    try {
      const params = new URLSearchParams();
      trCodes.forEach(code => params.append('tr_codes', code));
      const response = await api.get(`/api/screener/factor-loadings?${params.toString()}`);
      setFactorData(response.data);
      // Initialise equal weights for funds that have factor data
      const allFundsTr = [];
      Object.values(response.data.sac_groups || {}).forEach(funds => {
        funds.forEach(f => { if (!allFundsTr.includes(f.tr_code)) allFundsTr.push(f.tr_code); });
      });
      if (allFundsTr.length > 0) {
        const equalWeight = Math.round(100 / allFundsTr.length * 100) / 100;
        const weights = {};
        allFundsTr.forEach(tr => { weights[tr] = equalWeight; });
        setFactorWeights(weights);
      }
    } catch (error) {
      console.error('Error loading factor data:', error);
      setFactorData(null);
    } finally {
      setIsLoadingFactors(false);
    }
  };

  // Load factor loading for a single fund (modal)
  const loadFactorForFund = async (fund) => {
    if (!fund.tr_code) return;
    setFactorModalOpen(true);
    setIsLoadingFactorModal(true);
    setFactorModalData(null);
    try {
      const response = await api.get(`/api/screener/factor-loading?tr_code=${fund.tr_code}`);
      setFactorModalData({ ...response.data, request_name: fund.fund_name });
    } catch (error) {
      console.error('Error loading factor modal data:', error);
      setFactorModalData({ found: false, request_name: fund.fund_name });
    } finally {
      setIsLoadingFactorModal(false);
    }
  };

  const handleFactorWeightChange = (trCode, value) => {
    setFactorWeights(prev => ({ ...prev, [trCode]: parseFloat(value) || 0 }));
  };

  const closeFactorModal = () => {
    setFactorModalOpen(false);
    setFactorModalData(null);
  };


  // Diverging colour scale for beta values — blue positive, red negative
  const getBetaColor = (value) => {
    if (value === null || value === undefined) return 'transparent';
    const capped = Math.min(Math.abs(value), 1.5);
    const intensity = capped / 1.5;
    if (value > 0) {
      return `rgba(89, 155, 227, ${0.15 + intensity * 0.65})`; // blue
    } else {
      return `rgba(231, 76, 60, ${0.15 + intensity * 0.65})`; // red
    }
  };

  // Abbreviate factor names: Style_Growth -> S:Growth, Tilt_Quality -> T:Quality
  const abbreviateFactorName = (name) => {
    if (name.startsWith('Style_')) return 'S:' + name.substring(6);
    if (name.startsWith('Tilt_')) return 'T:' + name.substring(5);
    return name;
  };

  // Compute per-column min/max for column-wise conditional formatting
  const HEATMAP_VALUE_KEYS = ['mtd', 'return_1m', 'return_3m', 'return_6m', 'return_1yr', 'return_2yr', 'return_3yr', 'return_5yr'];

  const heatmapColumnStats = useMemo(() => {
    const stats = {};
    HEATMAP_VALUE_KEYS.forEach((key) => {
      const vals = filteredHeatmapData.map((r) => r[key]).filter((v) => v != null);
      if (vals.length === 0) { stats[key] = { min: 0, max: 0 }; return; }
      stats[key] = { min: Math.min(...vals), max: Math.max(...vals) };
    });
    return stats;
  }, [filteredHeatmapData]);

  // Sign-based heatmap colour: negatives get cascading shades of red, positives
  // cascading shades of green. Magnitude is scaled to the column's most-negative
  // / most-positive value so the deepest shade marks the extreme and values near
  // zero stay pale; exactly zero is neutral.
  const getHeatmapColor = (value, columnKey) => {
    if (value === null || value === undefined) return 'transparent';
    const { min, max } = heatmapColumnStats[columnKey] || { min: 0, max: 0 };
    const lerp = (a, b, t) => Math.round(a + (b - a) * t);

    if (value < 0) {
      const scale = min < 0 ? Math.abs(min) : Math.abs(value);
      const t = Math.min(1, Math.abs(value) / (scale || 1));   // 0 = pale, 1 = deepest
      // pale red → deep red
      return `rgb(${lerp(250, 178, t)}, ${lerp(216, 40, t)}, ${lerp(212, 38, t)})`;
    }
    if (value > 0) {
      const scale = max > 0 ? max : value;
      const t = Math.min(1, value / (scale || 1));
      // pale green → deep green
      return `rgb(${lerp(220, 48, t)}, ${lerp(242, 158, t)}, ${lerp(220, 74, t)})`;
    }
    return 'rgb(247, 247, 247)';   // exactly zero → neutral
  };

  // Filtering and sorting logic
  const sortedFunds = useMemo(() => {
    // First filter by search text
    let filtered = funds;
    if (searchText.trim()) {
      const search = searchText.toLowerCase().trim();
      filtered = funds.filter(fund =>
        fund.fund_name?.toLowerCase().includes(search) ||
        fund.tr_code?.toLowerCase().includes(search) ||
        fund.apir_code?.toLowerCase().includes(search)
      );
    }

    // Apply numeric range filters
    if (numericFilters.length > 0) {
      filtered = filtered.filter(fund =>
        numericFilters.every(({ column, operator, value }) => {
          const v = fund[column];
          if (v == null) return false;
          switch (operator) {
            case 'gte': return v >= value;
            case 'lte': return v <= value;
            case 'gt': return v > value;
            case 'lt': return v < value;
            default: return true;
          }
        })
      );
    }

    // Then sort
    const sorted = [...filtered];
    sorted.sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      if (typeof aVal === 'string') {
        return sortConfig.direction === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return sorted;
  }, [funds, sortConfig, searchText, numericFilters]);

  // Load average chart data when showAverage is toggled on or filtered funds change
  useEffect(() => {
    if (!showAverage) {
      setAverageChartData(null);
      return;
    }
    const trCodes = sortedFunds.map(f => f.tr_code).filter(Boolean);
    if (trCodes.length < 2) {
      setAverageChartData(null);
      return;
    }
    const loadAverage = async () => {
      setIsLoadingAverage(true);
      try {
        const params = new URLSearchParams();
        trCodes.forEach(code => params.append('tr_codes', code));
        const response = await api.get(`/api/screener/average-charts?${params.toString()}`);
        setAverageChartData(response.data);
      } catch (error) {
        console.error('Error loading average chart data:', error);
      } finally {
        setIsLoadingAverage(false);
      }
    };
    loadAverage();
  }, [showAverage, sortedFunds]);

  // Filter to only selected funds when hideUnselected is active
  const displayedFunds = useMemo(() => {
    if (!hideUnselected || selectedFunds.length === 0) return sortedFunds;
    const selectedCodes = new Set(selectedFunds.map(f => f.tr_code));
    return sortedFunds.filter(f => selectedCodes.has(f.tr_code));
  }, [sortedFunds, selectedFunds, hideUnselected]);

  // Calculate averages for all numeric columns
  const averages = useMemo(() => {
    if (sortedFunds.length === 0) return null;

    const numericFields = [
      'fee', 'mtd', 'return_1m', 'return_3m', 'return_6m', 'return_12m',
      'return_2yr', 'return_3yr', 'return_5yr', 'return_7yr', 'return_10yr',
      'vol_3yr', 'max_drawdown', 'tracking_error',
      'min_alpha', 'max_alpha', 'std_dev_alpha', 'alpha_ratio',
      'people', 'process', 'overall_rating'
    ];
    const result = {};

    numericFields.forEach(field => {
      const validValues = sortedFunds
        .map(f => f[field])
        .filter(v => v !== null && v !== undefined);
      result[field] = validValues.length > 0
        ? validValues.reduce((a, b) => a + b, 0) / validValues.length
        : null;
    });

    return result;
  }, [sortedFunds]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Numeric filter helpers
  let nextFilterId = useRef(1);
  const addNumericFilter = (column, operator, value) => {
    const parsed = parseFloat(value);
    if (isNaN(parsed)) return;
    setNumericFilters(prev => [...prev, { id: nextFilterId.current++, column, operator, value: parsed }]);
  };
  const removeNumericFilter = (id) => {
    setNumericFilters(prev => prev.filter(f => f.id !== id));
  };
  const clearNumericFilters = () => {
    setNumericFilters([]);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return '-';
    return `${(value * 100).toFixed(2)}%`;
  };

  const formatPercentDirect = (value) => {
    if (value === null || value === undefined) return '-';
    return `${value.toFixed(1)}%`;
  };

  const formatFee = (value) => {
    if (value === null || value === undefined) return '-';
    return `${value.toFixed(2)}%`;
  };

  const formatRating = (value) => {
    if (value === null || value === undefined) return '-';
    return value.toFixed(1);
  };

  // Export table to image via offscreen canvas
  const exportTableToImage = () => {
    const fmtVal = (v) => (v === null || v === undefined ? '-' : `${v.toFixed(1)}%`);
    const fmtFeeVal = (v) => (v === null || v === undefined ? '-' : `${v.toFixed(2)}%`);

    // Build columns (exclude Select, Returns/Alpha/Report buttons)
    const cols = [
      { key: 'fund_name', label: 'Fund', width: 260, align: 'left', fmt: (f) => `${f.fund_name || '-'}` },
      { key: 'fee', label: 'TER', width: 60, fmt: (f) => fmtFeeVal(f.fee) },
      ...(user?.is_drummond_user ? [{ key: 'mtd', label: 'MTD', width: 55, fmt: (f) => fmtVal(f.mtd) }] : []),
      { key: 'return_1m', label: '1m', width: 55, fmt: (f) => fmtVal(f.return_1m) },
      { key: 'return_3m', label: '3m', width: 55, fmt: (f) => fmtVal(f.return_3m) },
      { key: 'return_6m', label: '6m', width: 55, fmt: (f) => fmtVal(f.return_6m) },
      { key: 'return_12m', label: '12m', width: 55, fmt: (f) => fmtVal(f.return_12m) },
      { key: 'return_2yr', label: '2yr', width: 55, fmt: (f) => fmtVal(f.return_2yr) },
      { key: 'return_3yr', label: '3yr', width: 55, fmt: (f) => fmtVal(f.return_3yr) },
      { key: 'return_5yr', label: '5yr', width: 55, fmt: (f) => fmtVal(f.return_5yr) },
      { key: 'return_7yr', label: '7yr', width: 55, fmt: (f) => fmtVal(f.return_7yr) },
      { key: 'return_10yr', label: '10yr', width: 55, fmt: (f) => fmtVal(f.return_10yr) },
      { key: 'vol_3yr', label: '3yr Vol', width: 60, fmt: (f) => fmtVal(f.vol_3yr) },
      { key: 'max_drawdown', label: 'Max Draw', width: 68, fmt: (f) => fmtVal(f.max_drawdown) },
      { key: 'tracking_error', label: 'Tracking', width: 65, fmt: (f) => fmtVal(f.tracking_error) },
      { key: 'min_alpha', label: 'Min Alpha', width: 72, fmt: (f) => fmtVal(f.min_alpha) },
      { key: 'max_alpha', label: 'Max Alpha', width: 72, fmt: (f) => fmtVal(f.max_alpha) },
      { key: 'std_dev_alpha', label: 'Std Alpha', width: 72, fmt: (f) => fmtVal(f.std_dev_alpha) },
      { key: 'apir_code', label: 'APIR', width: 75, align: 'left', fmt: (f) => f.apir_code || '-' },
    ];

    const rowHeight = 22;
    const headerHeight = 28;
    const padding = 6;
    const totalWidth = cols.reduce((s, c) => s + c.width, 0) + padding * 2;

    // Combine rows: benchmarks + average + funds
    const allRows = [];
    benchmarks.forEach((bm) => allRows.push({ ...bm, _type: 'benchmark', fund_name: `BM: ${bm.fund_name}` }));
    if (averages && displayedFunds.length > 0) {
      allRows.push({ ...averages, _type: 'average', fund_name: `Average (${sortedFunds.length} funds)`, apir_code: '-' });
    }
    displayedFunds.forEach((f) => allRows.push({ ...f, _type: 'fund' }));

    const totalHeight = headerHeight + allRows.length * rowHeight + padding * 2;

    const canvas = document.createElement('canvas');
    const scale = 2; // retina
    canvas.width = totalWidth * scale;
    canvas.height = totalHeight * scale;
    const ctx = canvas.getContext('2d');
    ctx.scale(scale, scale);

    // Background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, totalWidth, totalHeight);

    // Header
    ctx.fillStyle = '#072040';
    ctx.fillRect(0, 0, totalWidth, headerHeight);
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 10px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    ctx.textBaseline = 'middle';
    let x = padding;
    cols.forEach((col) => {
      ctx.textAlign = col.align === 'left' ? 'left' : 'right';
      const tx = col.align === 'left' ? x + 4 : x + col.width - 4;
      ctx.fillText(col.label, tx, headerHeight / 2);
      x += col.width;
    });

    // Rows
    ctx.font = '10px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    allRows.forEach((row, i) => {
      const y = headerHeight + i * rowHeight;

      // Row background
      if (row._type === 'benchmark') {
        ctx.fillStyle = '#f0f4ff';
      } else if (row._type === 'average') {
        ctx.fillStyle = '#fff8e1';
      } else {
        ctx.fillStyle = i % 2 === 0 ? '#ffffff' : '#f9f9f9';
      }
      ctx.fillRect(0, y, totalWidth, rowHeight);

      // Cell values
      x = padding;
      cols.forEach((col) => {
        const val = col.fmt(row);
        const numKey = col.key;
        const numVal = row[numKey];

        // Colour for return/alpha cells
        if (numKey !== 'fund_name' && numKey !== 'apir_code' && numKey !== 'fee' &&
            numKey !== 'vol_3yr' && numKey !== 'tracking_error' && numKey !== 'std_dev_alpha') {
          if (typeof numVal === 'number' && numVal > 0) ctx.fillStyle = '#0d6832';
          else if (typeof numVal === 'number' && numVal < 0) ctx.fillStyle = '#b71c1c';
          else ctx.fillStyle = '#333333';
        } else {
          ctx.fillStyle = '#333333';
        }

        if (row._type === 'benchmark' || row._type === 'average') {
          ctx.font = 'bold 10px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
        } else {
          ctx.font = '10px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
        }

        ctx.textAlign = col.align === 'left' ? 'left' : 'right';
        const tx = col.align === 'left' ? x + 4 : x + col.width - 4;
        ctx.fillText(val, tx, y + rowHeight / 2);
        x += col.width;
      });

      // Row border
      ctx.strokeStyle = '#e0e0e0';
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(0, y + rowHeight);
      ctx.lineTo(totalWidth, y + rowHeight);
      ctx.stroke();
    });

    // Download
    const link = document.createElement('a');
    link.download = `fund-screener-${new Date().toISOString().slice(0, 10)}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  // Format refresh date as dd-mmm-yyyy (e.g., "03-Dec-2024")
  const formatRefreshDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch {
      return dateStr;
    }
  };

  // Format a 'YYYY-MM-DD' string as dd-Mon-yyyy WITHOUT new Date() (timezone-proof)
  const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const formatDateOnly = (d) => {
    if (!d) return null;
    const [y, m, day] = String(d).split('-');
    return (y && m && day) ? `${day}-${MONTHS_SHORT[+m - 1]}-${y}` : d;
  };

  // Build chart config
  const buildChartConfig = (dataArray, title, isPercentage = true, avgSeries = null) => {
    const hasData = dataArray && dataArray.length > 0;
    const hasAvg = avgSeries && avgSeries.length > 0;
    if (!hasData && !hasAvg) return null;

    // Get all unique dates
    const allDates = new Set();
    if (dataArray) {
      dataArray.forEach(fund => {
        fund.data.forEach(d => allDates.add(d.date));
      });
    }
    if (avgSeries) {
      avgSeries.forEach(d => allDates.add(d.date));
    }
    const sortedDates = Array.from(allDates).sort();

    const datasets = (dataArray || []).map((fund, index) => {
      const dataMap = new Map(fund.data.map(d => [d.date, d.value]));
      return {
        label: fund.fund_name,
        data: sortedDates.map(date => dataMap.get(date) ?? null),
        borderColor: CHART_COLORS[index % CHART_COLORS.length],
        backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.1,
        spanGaps: true,
      };
    });

    // Add average series as dashed line
    if (avgSeries && avgSeries.length > 0) {
      const avgMap = new Map(avgSeries.map(d => [d.date, d.value]));
      datasets.push({
        label: `Average (${sortedFunds.length} funds)`,
        data: sortedDates.map(date => avgMap.get(date) ?? null),
        borderColor: '#2CA02C',
        backgroundColor: '#2CA02C',
        borderWidth: 2.5,
        borderDash: [6, 3],
        pointRadius: 0,
        tension: 0.1,
        spanGaps: true,
      });
    }

    return {
      labels: sortedDates.map(d => d.substring(0, 7)), // YYYY-MM format
      datasets,
    };
  };

  // Hide legend when more than 6 funds selected
  const showLegend = selectedFunds.length <= 6;

  const chartOptions = (title) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: showLegend,
        position: 'bottom',
        labels: { boxWidth: 12, padding: 15 }
      },
      title: {
        display: true,
        text: showLegend ? title : `${title} (${selectedFunds.length} funds)`,
        font: { size: 14, weight: 'bold' },
        color: theme.primary_color || '#072040'
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.parsed.y.toFixed(1)}%`
        }
      }
    },
    scales: {
      x: {
        ticks: { maxTicksLimit: 12 }
      },
      y: {
        ticks: {
          callback: (value) => `${value.toFixed(1)}%`
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  });

  // Build calendar year returns bar chart config
  const buildCalendarReturnsConfig = (calendarReturns) => {
    if (!calendarReturns || calendarReturns.length === 0) return null;

    // Get all unique years
    const allYears = new Set();
    calendarReturns.forEach(fund => {
      fund.returns.forEach(r => allYears.add(r.year));
    });
    const sortedYears = Array.from(allYears).sort();

    const datasets = calendarReturns.map((fund, index) => {
      const yearMap = new Map(fund.returns.map(r => [r.year, r.value]));
      return {
        label: fund.fund_name,
        data: sortedYears.map(year => yearMap.get(year) ?? null),
        backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
        borderColor: CHART_COLORS[index % CHART_COLORS.length],
        borderWidth: 1,
      };
    });

    return { labels: sortedYears, datasets };
  };

  const calendarChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: showLegend, position: 'bottom', labels: { boxWidth: 12, padding: 15 } },
      title: {
        display: true,
        text: showLegend ? 'Calendar Year Returns' : `Calendar Year Returns (${selectedFunds.length} funds)`,
        font: { size: 14, weight: 'bold' },
        color: theme.primary_color || '#072040'
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.parsed.y.toFixed(1)}%`
        }
      }
    },
    scales: {
      y: {
        ticks: { callback: (value) => `${value.toFixed(1)}%` }
      }
    }
  };

  // Build risk/reward scatter chart config
  const buildRiskRewardConfig = (riskRewardData) => {
    if (!riskRewardData || riskRewardData.length === 0) return null;

    const datasets = riskRewardData.map((fund, index) => ({
      label: fund.fund_name,
      data: fund.risk != null && fund.return_value != null
        ? [{ x: fund.risk, y: fund.return_value }]
        : [],
      backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
      pointRadius: 8,
      pointHoverRadius: 10,
    }));

    return { datasets };
  };

  const riskRewardChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: showLegend, position: 'bottom', labels: { boxWidth: 12, padding: 15 } },
      title: {
        display: true,
        text: showLegend ? 'Risk/Reward - 3 Years' : `Risk/Reward - 3 Years (${selectedFunds.length} funds)`,
        font: { size: 14, weight: 'bold' },
        color: theme.primary_color || '#072040'
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const point = context.raw;
            return `${context.dataset.label}: Risk ${point.x?.toFixed(1)}%, Return ${point.y?.toFixed(1)}%`;
          }
        }
      }
    },
    scales: {
      x: {
        title: { display: true, text: 'Risk (Std Dev %)' },
        ticks: { callback: (value) => `${value.toFixed(1)}%` }
      },
      y: {
        title: { display: true, text: 'Return %' },
        ticks: { callback: (value) => `${value.toFixed(1)}%` }
      }
    }
  };

  // Fullscreen chart options with zoom enabled
  const getFullscreenChartOptions = (baseOptions, title) => ({
    ...baseOptions,
    maintainAspectRatio: false,
    plugins: {
      ...baseOptions.plugins,
      title: {
        display: true,
        text: title,
        font: { size: 18, weight: 'bold' },
        color: theme.primary_color || '#072040'
      },
      zoom: {
        pan: {
          enabled: true,
          mode: 'xy',
        },
        zoom: {
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true,
          },
          mode: 'xy',
        },
      },
    },
  });

  // Open chart in fullscreen modal
  const openFullscreenChart = (chartType, title, data, options) => {
    setFullscreenChart({ type: chartType, title, data, options: getFullscreenChartOptions(options, title) });
  };

  // Reset zoom on fullscreen chart
  const resetZoom = () => {
    if (fullscreenChartRef.current) {
      fullscreenChartRef.current.resetZoom();
    }
  };

  // Get correlation colour based on value
  const getCorrelationColor = (value) => {
    if (value >= 0.8) return '#91CF9B'; // Green - high correlation
    if (value >= 0.4) return '#f0a500'; // Yellow/orange - moderate
    return '#E74C3C'; // Red - low/negative
  };

  // Calculate discrete monthly alpha from cumulative alpha data (geometric)
  const calculateDiscreteAlpha = (cumulativeData) => {
    if (!cumulativeData || cumulativeData.length === 0) return {};

    // Sort by date
    const sorted = [...cumulativeData].sort((a, b) => a.date.localeCompare(b.date));

    // Calculate discrete (geometric percentage change from previous month)
    const discrete = [];
    for (let i = 1; i < sorted.length; i++) {
      const current = sorted[i];
      const previous = sorted[i - 1];
      const year = current.date.substring(0, 4);
      const month = parseInt(current.date.substring(5, 7)); // 1-12

      // Geometric calculation for discrete monthly alpha
      // Values arrive as percentages (e.g., 5.0 = 5%) from backend (* 100 applied in charts endpoint)
      const currentVal = current.value;
      const previousVal = previous.value;
      const discreteValue = ((1 + currentVal / 100) / (1 + previousVal / 100) - 1) * 100;

      discrete.push({
        year,
        month,
        value: discreteValue
      });
    }

    // Organize into year/month grid
    const grid = {}; // { "2024": { 1: value, 2: value, ... }, ... }
    discrete.forEach(d => {
      if (!grid[d.year]) grid[d.year] = {};
      grid[d.year][d.month] = d.value;
    });

    return grid;
  };

  // Filter Panel Component
  // Inline numeric filter "Add" row
  // Column filter popover sub-component
  const ColumnFilterPopover = ({ column, onClose }) => {
    const colDef = numericFilterColumns.find(c => c.key === column);
    const existing = numericFilters.find(f => f.column === column);
    const [op, setOp] = useState(existing?.operator || 'gte');
    const [val, setVal] = useState(existing?.value?.toString() || '');
    const popoverRef = useRef(null);

    // Close on click outside
    useEffect(() => {
      const handleClickOutside = (e) => {
        if (popoverRef.current && !popoverRef.current.contains(e.target)) {
          onClose();
        }
      };
      const handleEscape = (e) => {
        if (e.key === 'Escape') onClose();
      };
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }, [onClose]);

    const handleApply = () => {
      const parsed = parseFloat(val);
      if (val === '' || isNaN(parsed)) return;
      // Replace existing filter for this column in one update
      setNumericFilters(prev => [
        ...prev.filter(f => f.column !== column),
        { id: nextFilterId.current++, column, operator: op, value: parsed }
      ]);
      onClose();
    };

    const handleRemove = () => {
      const existingFilter = numericFilters.find(f => f.column === column);
      if (existingFilter) removeNumericFilter(existingFilter.id);
      onClose();
    };

    return (
      <div className="column-filter-popover" ref={popoverRef} onClick={e => e.stopPropagation()}>
        <div className="popover-section popover-sort-section">
          <button
            className={`popover-sort-btn ${sortConfig.key === column && sortConfig.direction === 'asc' ? 'active' : ''}`}
            onClick={() => { setSortConfig({ key: column, direction: 'asc' }); onClose(); }}
          >
            ↑ Sort Low to High
          </button>
          <button
            className={`popover-sort-btn ${sortConfig.key === column && sortConfig.direction === 'desc' ? 'active' : ''}`}
            onClick={() => { setSortConfig({ key: column, direction: 'desc' }); onClose(); }}
          >
            ↓ Sort High to Low
          </button>
        </div>
        <div className="popover-divider" />
        <div className="popover-section popover-filter-section">
          <label className="popover-filter-label">Filter {colDef?.label || column}</label>
          <div className="popover-filter-row">
            <select value={op} onChange={e => setOp(e.target.value)} className="popover-op-select">
              {OPERATOR_OPTIONS.map(o => (
                <option key={o.key} value={o.key} title={o.title}>{o.label}</option>
              ))}
            </select>
            <div className="popover-input-wrap">
              <input
                type="number"
                step="any"
                className="popover-value-input"
                placeholder={colDef?.pct ? 'e.g. 5' : 'Value'}
                value={val}
                onChange={e => setVal(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleApply(); }}
                autoFocus
              />
              {colDef?.pct && <span className="popover-input-suffix">%</span>}
            </div>
          </div>
          <div className="popover-actions">
            <button className="popover-apply-btn" onClick={handleApply}>Apply</button>
            {existing && (
              <button className="popover-remove-btn" onClick={handleRemove}>Remove Filter</button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const FilterPanel = ({ name, label, options, isNumeric = false, columns = 2, formatOption }) => (
    <div className={`filter-group filter-group-${name}`}>
      <div className="filter-header">
        <span className="filter-label">{label}</span>
        <button
          className="clear-filter-btn"
          onClick={() => clearFilter(name)}
          style={{ visibility: filters[name].length > 0 ? 'visible' : 'hidden' }}
        >
          Clear
        </button>
      </div>
      <div className="filter-options" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
        {options?.map(option => (
          <label key={String(option)} className="filter-option">
            <input
              type="checkbox"
              checked={filters[name].includes(option)}
              onChange={() => handleFilterChange(name, option)}
            />
            <span>{formatOption ? formatOption(option) : option}</span>
          </label>
        ))}
      </div>
    </div>
  );

  // Check if column is filterable (numeric)
  const isFilterableColumn = (column) => numericFilterColumns.some(c => c.key === column);
  const hasActiveFilter = (column) => numericFilters.some(f => f.column === column);

  // Table Header Component with popover support for numeric columns
  const TableHeader = ({ column, label, title, sortable = true }) => {
    const filterable = isFilterableColumn(column);
    const activeFilter = hasActiveFilter(column);
    const isPopoverOpen = filterPopup === column;

    const handleHeaderClick = () => {
      if (!sortable) return;
      if (filterable) {
        // Toggle popover for filterable columns
        setFilterPopup(prev => prev === column ? null : column);
      } else {
        // Sort-only for non-filterable columns
        handleSort(column);
      }
    };

    return (
      <th
        className={`${sortable ? 'sortable' : ''} ${activeFilter ? 'th-has-filter' : ''}`}
        onClick={handleHeaderClick}
        title={title || label}
      >
        <span className="th-label-wrap">
          {label}
          {sortConfig.key === column && (
            <span className="sort-indicator">
              {sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}
            </span>
          )}
          {filterable && (
            <span className={`th-filter-icon ${activeFilter ? 'active' : ''}`}>
              ▾
            </span>
          )}
        </span>
        {isPopoverOpen && (
          <ColumnFilterPopover
            column={column}
            onClose={() => setFilterPopup(null)}
          />
        )}
      </th>
    );
  };

  if (isLoadingFilters) {
    return (
      <div className="fund-screener-container">
        <div className="loading-spinner">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fund-screener-container">
      <div className="screener-title-row">
        <h2>Fund Screener</h2>
        <span className="results-count">
          {numericFilters.length > 0 || searchText.trim() || hideUnselected
            ? `${displayedFunds.length} of ${funds.length} funds`
            : `${displayedFunds.length} funds`}
        </span>
        {refreshDate && (
          <span className="refresh-date">({formatRefreshDate(refreshDate)})</span>
        )}
        <div className="toolbar-controls">
          {/* Universe-wide time machine — restates the WHOLE screener at a historical month-end.
              Lives in the controls strip (a view-mode control), so it never wraps below the
              attribute filters. Default option shows the latest month-end (live /funds view). */}
          {asAtDates.length > 0 && (
            <div className="asat-selector" title="Restate the whole screener as at a historical month-end">
              <label htmlFor="asat-select">As at:</label>
              <select
                id="asat-select"
                className="asat-select"
                value={asAtDate || ''}
                onChange={handleAsAtChange}
              >
                <option value="">{formatDateOnly(asAtDates[0])}</option>
                {asAtDates.slice(1).map(d => (
                  <option key={d} value={d}>{formatDateOnly(d)}</option>
                ))}
              </select>
            </div>
          )}
          {isDrummondUser && (
            <button className="btn btn-methodology" onClick={() => setFactorMethodOpen(true)} title="Factor Risk Model Methodology">
              <svg viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/>
              </svg>
              <span>Methodology</span>
            </button>
          )}
          <button className="btn btn-heatmap" onClick={loadHeatmapData} title="Manager Performance Heatmap">
            <svg viewBox="0 0 16 16" fill="currentColor">
              <path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2zm2 0v3h3V2H2zm0 4v3h3V6H2zm0 4v3h3v-3H2zm4-8v3h3V2H6zm0 4v3h3V6H6zm0 4v3h3v-3H6zm4-8v3h3V2h-3zm0 4v3h3V6h-3zm0 4v3h3v-3h-3z"/>
            </svg>
            <span>Heatmap</span>
          </button>
          <button className="btn btn-style-returns" onClick={openStyleReturnsModal} title="Style Cumulative Returns">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 1v14h14M3 11l3-4 3 2 4-6"/>
            </svg>
            <span>Style Returns</span>
          </button>
          <button className="btn btn-export" onClick={exportTableToImage} title="Export table as image">
            <svg viewBox="0 0 16 16" fill="currentColor">
              <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
              <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
            </svg>
            <span>Export</span>
          </button>
          <label className="flagship-toggle" title="Show only flagship funds">
            <input
              type="checkbox"
              checked={flagshipOnly}
              onChange={(e) => setFlagshipOnly(e.target.checked)}
            />
            <span>Flagship Only</span>
          </label>
          <div className="search-box">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search by fund or APIR..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            {searchText && (
              <button className="search-clear" onClick={() => setSearchText('')}>
                &times;
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filter Cards */}
      <div className="screener-toolbar">
        <div className="toolbar-filters">
          {/* Asset Class - grouped into Growth and Defensive */}
          {(() => {
            const defensiveSet = new Set(filterOptions?.defensive_classes || []);
            const allOptions = filterOptions?.asset_class || [];
            const growthOptions = allOptions.filter(o => !defensiveSet.has(o));
            const defensiveOptions = allOptions.filter(o => defensiveSet.has(o));
            return (
              <div className="filter-group filter-group-asset_class">
                <div className="filter-header">
                  <span className="filter-label">Asset Class</span>
                  <button
                    className="clear-filter-btn"
                    onClick={() => clearFilter('asset_class')}
                    style={{ visibility: filters.asset_class.length > 0 ? 'visible' : 'hidden' }}
                  >
                    Clear
                  </button>
                </div>
                {growthOptions.length > 0 && (
                  <>
                    <div style={{ fontSize: '0.65rem', fontWeight: 600, color: 'var(--gray-color)', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Growth</div>
                    <div className="filter-options" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: '2px 8px' }}>
                      {growthOptions.map(option => (
                        <label key={option} className="filter-option">
                          <input
                            type="checkbox"
                            checked={filters.asset_class.includes(option)}
                            onChange={() => handleFilterChange('asset_class', option)}
                          />
                          <span>{option}</span>
                        </label>
                      ))}
                    </div>
                  </>
                )}
                {defensiveOptions.length > 0 && (
                  <>
                    <div style={{ borderTop: '1px solid rgba(0, 51, 102, 0.1)', margin: '4px 0' }} />
                    <div style={{ fontSize: '0.65rem', fontWeight: 600, color: 'var(--gray-color)', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Defensive</div>
                    <div className="filter-options" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: '2px 8px' }}>
                      {defensiveOptions.map(option => (
                        <label key={option} className="filter-option">
                          <input
                            type="checkbox"
                            checked={filters.asset_class.includes(option)}
                            onChange={() => handleFilterChange('asset_class', option)}
                          />
                          <span>{option}</span>
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })()}
          <FilterPanel name="style" label="Style" options={filterOptions?.style} columns={3} />
          <FilterPanel name="tilt" label="Tilt" options={filterOptions?.tilt} columns={3} />
          <div className="filter-group-pair">
            <FilterPanel name="hedged" label="Hedged" options={filterOptions?.hedged} isNumeric columns={1} />
            {isDrummondUser && <FilterPanel name="held" label="Held" options={filterOptions?.held} isNumeric columns={1} />}
          </div>
        </div>
      </div>

      {/* Historical-mode disclaimer — same wording as the Returns modal */}
      {asAtDate && (
        <div className="asat-disclaimer">
          Showing the screener as at {formatDateOnly(asAtDate)}. Rolling returns are recomputed
          point-in-time as at this month-end from geometrically compounded LSEG monthly data (plus
          historical track records where available). Figures reflect the latest available data
          restated to this date, not the values originally published then. Alpha is measured
          against a Drummond-assigned representative passive fund/ETF for the sub-asset class, which
          may differ from the fund's stated benchmark. Volatility and max drawdown are
          Drummond-computed approximations (a full 3 years of history required). MTD, tracking
          error, streak, trend and factor columns are not shown for historical dates. Only funds
          with reported data as at this date are shown.
        </div>
      )}

      {/* Active Filter Chips */}
      {numericFilters.length > 0 && (
        <div className="active-filter-chips">
          {numericFilters.map(f => {
            const col = numericFilterColumns.find(c => c.key === f.column);
            const op = OPERATOR_OPTIONS.find(o => o.key === f.operator);
            return (
              <span key={f.id} className="numeric-chip">
                {col?.label} {op?.label} {f.value}{col?.pct ? '%' : ''}
                <button className="chip-remove" onClick={() => removeNumericFilter(f.id)}>&times;</button>
              </span>
            );
          })}
          <button className="numeric-clear-all" onClick={clearNumericFilters}>Clear All</button>
        </div>
      )}

      {isLoadingFunds ? (
        <div className="loading-spinner">
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="table-container">
          {selectedFunds.length > 0 && (
            <div className="selection-info" style={{ marginBottom: '0.5rem' }}>
              <span>{selectedFunds.length} selected</span>
              <button
                className={`btn btn-sm ms-2 ${hideUnselected ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setHideUnselected(prev => !prev)}
                title={hideUnselected ? 'Show all funds' : 'Show only selected funds'}
              >
                {hideUnselected ? 'Show All' : 'Hide Unselected'}
              </button>
              <button className="btn btn-sm btn-outline-secondary ms-2" onClick={clearSelection}>
                Clear Selection
              </button>
            </div>
          )}
            <table className="funds-table">
              <thead ref={theadRef}>
                <tr>
                  <th className="select-col">Select</th>
                  <TableHeader column="fund_name" label="Fund" />
                  <th className="actions-col">Actions</th>
                  <TableHeader column="fee" label="TER" title="Total Expense Ratio. Sourced from LSEG (TR.FundTER)." />
                  {user?.is_drummond_user && <TableHeader column="mtd" label="MTD" title="Month-to-Date return. Calculated from LSEG bid prices as (latest_bid / last_month_end_bid - 1) × 100. Uses daily bid data from the last 60 days, forward-filled and resampled daily." />}
                  <TableHeader column="return_1m" label="1m" title="Rolling 1-month return. Calculated from geometrically compounded cumulative returns built from LSEG monthly since-inception data (+ historical track records where available). Formula: ((1 + cum_end) / (1 + cum_start) - 1) × 100." />
                  <TableHeader column="return_3m" label="3m" title="Rolling 3-month return. Calculated from geometrically compounded cumulative returns: ((1 + cum_end) / (1 + cum_start) - 1) × 100 over 3 months." />
                  <TableHeader column="return_6m" label="6m" title="Rolling 6-month return. Calculated from geometrically compounded cumulative returns: ((1 + cum_end) / (1 + cum_start) - 1) × 100 over 6 months." />
                  <TableHeader column="return_12m" label="12m" title="Rolling 12-month return. Calculated from geometrically compounded cumulative returns: ((1 + cum_end) / (1 + cum_start) - 1) × 100 over 12 months." />
                  <TableHeader column="return_2yr" label="2yr" title="Annualised 2-year return. Calculated from cumulative returns: (((1 + cum_end) / (1 + cum_start))^(12/24) - 1) × 100. Geometric annualisation over 24 months." />
                  <TableHeader column="return_3yr" label="3yr" title="Annualised 3-year return. Calculated from cumulative returns: (((1 + cum_end) / (1 + cum_start))^(12/36) - 1) × 100. Geometric annualisation over 36 months." />
                  <TableHeader column="return_5yr" label="5yr" title="Annualised 5-year return. Calculated from cumulative returns: (((1 + cum_end) / (1 + cum_start))^(12/60) - 1) × 100. Geometric annualisation over 60 months." />
                  <TableHeader column="return_7yr" label="7yr" title="Annualised 7-year return. Calculated from cumulative returns: (((1 + cum_end) / (1 + cum_start))^(12/84) - 1) × 100. Geometric annualisation over 84 months." />
                  <TableHeader column="return_10yr" label="10yr" title="Annualised 10-year return. Calculated from cumulative returns: (((1 + cum_end) / (1 + cum_start))^(12/120) - 1) × 100. Geometric annualisation over 120 months." />
                  <TableHeader column="vol_3yr" label="3yr Vol" title="3-year annualised standard deviation. Sourced from LSEG (TR.FundStdDev3Year)." />
                  {isDrummondUser && <TableHeader column="intercept" label="Intercept" title="Monthly alpha-of-alpha from the factor risk model (OLS regression). Represents the average monthly return not explained by the fund's factor exposures (style, tilt, asset class betas). Positive = the fund generates excess return beyond what its factor tilts would predict. Shown as % per month." />}
                  <TableHeader column="max_drawdown" label="Max Drawdown" title="Maximum drawdown over 3 years. Sourced from LSEG (TR.FundMaxDrawDown3Year)." />
                  <TableHeader column="tracking_error" label="Tracking Error" title="Annualised tracking error since inception. Calculated as the standard deviation of monthly alpha (fund return minus benchmark return) × √12. Alpha is measured against a Drummond-assigned representative passive fund/ETF for each sub-asset class, which may differ from the fund's stated benchmark." />
                  <TableHeader column="min_alpha" label="Min Alpha" title="Minimum of the rolling 1-year alpha series. Rolling 1yr alpha: ((1 + cum_end) / (1 + cum_start) - 1) × 100 over 12 months. Alpha is measured against the Drummond-assigned representative passive fund/ETF for the sub-asset class, not the fund's stated benchmark." />
                  <TableHeader column="max_alpha" label="Max Alpha" title="Maximum of the rolling 1-year alpha series. Rolling 1yr alpha: ((1 + cum_end) / (1 + cum_start) - 1) × 100 over 12 months. Alpha is measured against the Drummond-assigned representative passive fund/ETF for the sub-asset class, not the fund's stated benchmark." />
                  <TableHeader column="std_dev_alpha" label="Std Dev Alpha" title="Standard deviation of the rolling 1-year alpha series. Lower values indicate more consistent relative performance. Alpha is measured against the Drummond-assigned representative passive fund/ETF for the sub-asset class, not the fund's stated benchmark." />
                  <TableHeader column="alpha_streak" label="Streak" title="Consecutive months of same-sign alpha. Cumulative alpha converted to discrete monthly: ((1 + curr) / (1 + prev)) - 1, then counting consecutive months with the same sign from the most recent month. Alpha is measured against the Drummond-assigned passive fund/ETF for the sub-asset class." />
                  <TableHeader column="pctile_3yr" label="Pctile" title="Percentile rank of the fund's 3-year annualised return within its asset class peer group. Calculated as (rank / total funds in class) × 100. Higher = better relative performance. 90th percentile means the fund outperformed 90% of peers." />
                  <TableHeader column="alpha_slope_12m" label="Trend" title="12-month alpha trend slope. Linear regression (OLS) on the last 12 months of cumulative alpha: slope = (n×Σxy - Σx×Σy) / (n×Σx² - (Σx)²). Positive = alpha improving, negative = deteriorating. Alpha is measured against the Drummond-assigned passive fund/ETF for the sub-asset class." />
                  <TableHeader column="apir_code" label="APIR Code" />
                </tr>
                {/* Benchmark Rows - inside thead so they freeze with header */}
                {benchmarks.map((bm, index) => {
                  const isBmSelected = selectedFunds.some(f => f.tr_code === bm.tr_code);
                  const stickyTop = frozenOffsets.benchmarks[index] ?? 0;
                  return (
                  <tr
                    key={`benchmark-${bm.tr_code || index}`}
                    className={`benchmark-row${isBmSelected ? ' selected' : ''}`}
                    style={{ '--sticky-top': `${stickyTop}px` }}
                    onClick={() => bm.tr_code && toggleFundSelection(bm)}
                  >
                    <td className="select-col">
                      <input
                        type="checkbox"
                        checked={isBmSelected}
                        onChange={() => {}}
                        disabled={!bm.tr_code}
                      />
                    </td>
                    <td className="fund-name"><strong>BM: {bm.fund_name}</strong></td>
                    <td className="actions-col">-</td>
                    <td>{formatFee(bm.fee)}</td>
                    {user?.is_drummond_user && (
                      <td className={bm.mtd > 0 ? 'positive' : bm.mtd < 0 ? 'negative' : ''}>
                        {formatPercentDirect(bm.mtd)}
                      </td>
                    )}
                    <td className={bm.return_1m > 0 ? 'positive' : bm.return_1m < 0 ? 'negative' : ''}>{formatPercentDirect(bm.return_1m)}</td>
                    <td className={bm.return_3m > 0 ? 'positive' : bm.return_3m < 0 ? 'negative' : ''}>{formatPercentDirect(bm.return_3m)}</td>
                    <td className={bm.return_6m > 0 ? 'positive' : bm.return_6m < 0 ? 'negative' : ''}>{formatPercentDirect(bm.return_6m)}</td>
                    <td className={bm.return_12m > 0 ? 'positive' : bm.return_12m < 0 ? 'negative' : ''}>{formatPercentDirect(bm.return_12m)}</td>
                    <td className={bm.return_2yr > 0 ? 'positive' : bm.return_2yr < 0 ? 'negative' : ''}>{formatPercentDirect(bm.return_2yr)}</td>
                    <td className={bm.return_3yr > 0 ? 'positive' : bm.return_3yr < 0 ? 'negative' : ''}>{formatPercentDirect(bm.return_3yr)}</td>
                    <td className={bm.return_5yr > 0 ? 'positive' : bm.return_5yr < 0 ? 'negative' : ''}>{formatPercentDirect(bm.return_5yr)}</td>
                    <td className={bm.return_7yr > 0 ? 'positive' : bm.return_7yr < 0 ? 'negative' : ''}>{formatPercentDirect(bm.return_7yr)}</td>
                    <td className={bm.return_10yr > 0 ? 'positive' : bm.return_10yr < 0 ? 'negative' : ''}>{formatPercentDirect(bm.return_10yr)}</td>
                    <td>{formatPercentDirect(bm.vol_3yr)}</td>
                    {isDrummondUser && <td>-</td>}
                    <td className="negative">{formatPercentDirect(bm.max_drawdown)}</td>
                    <td>{formatPercentDirect(bm.tracking_error)}</td>
                    <td>{formatPercentDirect(bm.min_alpha)}</td>
                    <td>{formatPercentDirect(bm.max_alpha)}</td>
                    <td>{formatPercentDirect(bm.std_dev_alpha)}</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td>{bm.apir_code || '-'}</td>
                  </tr>
                  );
                })}

                {/* Average Row */}
                {averages && displayedFunds.length > 0 && (
                  <tr className="average-row" style={{ '--sticky-top': `${frozenOffsets.average}px` }}>
                    <td className="select-col">
                      <input
                        type="checkbox"
                        checked={showAverage}
                        onChange={() => setShowAverage(prev => !prev)}
                        title="Show average in charts"
                      />
                    </td>
                    <td className="fund-name"><strong>Average ({sortedFunds.length} funds)</strong></td>
                    <td className="actions-col">-</td>
                    <td>{formatFee(averages.fee)}</td>
                    {user?.is_drummond_user && (
                      <td className={averages.mtd > 0 ? 'positive' : averages.mtd < 0 ? 'negative' : ''}>
                        {formatPercentDirect(averages.mtd)}
                      </td>
                    )}
                    <td className={averages.return_1m > 0 ? 'positive' : averages.return_1m < 0 ? 'negative' : ''}>{formatPercentDirect(averages.return_1m)}</td>
                    <td className={averages.return_3m > 0 ? 'positive' : averages.return_3m < 0 ? 'negative' : ''}>{formatPercentDirect(averages.return_3m)}</td>
                    <td className={averages.return_6m > 0 ? 'positive' : averages.return_6m < 0 ? 'negative' : ''}>{formatPercentDirect(averages.return_6m)}</td>
                    <td className={averages.return_12m > 0 ? 'positive' : averages.return_12m < 0 ? 'negative' : ''}>{formatPercentDirect(averages.return_12m)}</td>
                    <td className={averages.return_2yr > 0 ? 'positive' : averages.return_2yr < 0 ? 'negative' : ''}>{formatPercentDirect(averages.return_2yr)}</td>
                    <td className={averages.return_3yr > 0 ? 'positive' : averages.return_3yr < 0 ? 'negative' : ''}>{formatPercentDirect(averages.return_3yr)}</td>
                    <td className={averages.return_5yr > 0 ? 'positive' : averages.return_5yr < 0 ? 'negative' : ''}>{formatPercentDirect(averages.return_5yr)}</td>
                    <td className={averages.return_7yr > 0 ? 'positive' : averages.return_7yr < 0 ? 'negative' : ''}>{formatPercentDirect(averages.return_7yr)}</td>
                    <td className={averages.return_10yr > 0 ? 'positive' : averages.return_10yr < 0 ? 'negative' : ''}>{formatPercentDirect(averages.return_10yr)}</td>
                    <td>{formatPercentDirect(averages.vol_3yr)}</td>
                    {isDrummondUser && <td>-</td>}
                    <td className="negative">{formatPercentDirect(averages.max_drawdown)}</td>
                    <td>{formatPercentDirect(averages.tracking_error)}</td>
                    <td>{formatPercentDirect(averages.min_alpha)}</td>
                    <td>{formatPercentDirect(averages.max_alpha)}</td>
                    <td>{formatPercentDirect(averages.std_dev_alpha)}</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                  </tr>
                )}
              </thead>
              <tbody>
                {/* Fund Rows */}
                {displayedFunds.map((fund, index) => {
                  const isSelected = selectedFunds.some(f => f.tr_code === fund.tr_code);
                  // No benchmark-alpha series exists for this fund (typical for private market
                  // funds — no assigned alpha benchmark), so Alpha/Peer have nothing to show.
                  // Checked from fields already in the /funds response — no extra requests.
                  const hasAlphaData = fund.min_alpha != null || fund.tracking_error != null || fund.alpha_streak != null;
                  return (
                    <tr
                      key={fund.tr_code || index}
                      className={isSelected ? 'selected' : ''}
                      onClick={() => fund.tr_code && toggleFundSelection(fund)}
                    >
                      <td className="select-col">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          disabled={!fund.tr_code}
                        />
                      </td>
                      <td className="fund-name">
                        {fund.fund_name}
                      </td>
                      <td className="actions-col">
                        {fund.tr_code ? (
                          <div className="actions-btn-group">
                            <button
                              className="action-btn action-btn-returns"
                              onClick={(e) => openReturnsModal(fund, e)}
                              title="View Point-in-Time Returns"
                            >
                              Returns
                            </button>
                            <button
                              className="action-btn action-btn-alpha"
                              disabled={!hasAlphaData}
                              onClick={(e) => {
                                e.stopPropagation();
                                loadAlphaForFund(fund);
                              }}
                              title={hasAlphaData ? 'View Monthly Alpha' : 'No benchmark alpha data for this fund'}
                            >
                              Alpha
                            </button>
                            <button
                              className="action-btn action-btn-peer"
                              disabled={!hasAlphaData}
                              onClick={(e) => loadAlphaRegime(fund, e)}
                              title={hasAlphaData ? 'View Alpha vs Peer Group' : 'No benchmark alpha data for this fund'}
                            >
                              Peer
                            </button>
                            {isDrummondUser && ['Australian Equities', 'International Equities', 'EM Equities'].includes(fund.asset_class) && (
                              <button
                                className="action-btn action-btn-factors"
                                onClick={(e) => { e.stopPropagation(); loadFactorForFund(fund); }}
                                title="View Factor Loadings"
                              >
                                Factors
                              </button>
                            )}
                            {user?.is_drummond_user && fund.has_report && (
                              <button
                                className="action-btn action-btn-report"
                                onClick={(e) => handleViewReport(fund, e)}
                                title="View Research Report"
                              >
                                Report
                              </button>
                            )}
                          </div>
                        ) : <span className="no-data">-</span>}
                      </td>
                      <td>{formatFee(fund.fee)}</td>
                      {user?.is_drummond_user && (
                        <td className={fund.mtd > 0 ? 'positive' : fund.mtd < 0 ? 'negative' : ''}>
                          {formatPercentDirect(fund.mtd)}
                        </td>
                      )}
                      <td className={fund.return_1m > 0 ? 'positive' : fund.return_1m < 0 ? 'negative' : ''}>
                        {formatPercentDirect(fund.return_1m)}
                      </td>
                      <td className={fund.return_3m > 0 ? 'positive' : fund.return_3m < 0 ? 'negative' : ''}>
                        {formatPercentDirect(fund.return_3m)}
                      </td>
                      <td className={fund.return_6m > 0 ? 'positive' : fund.return_6m < 0 ? 'negative' : ''}>
                        {formatPercentDirect(fund.return_6m)}
                      </td>
                      <td className={fund.return_12m > 0 ? 'positive' : fund.return_12m < 0 ? 'negative' : ''}>
                        {formatPercentDirect(fund.return_12m)}
                      </td>
                      <td className={fund.return_2yr > 0 ? 'positive' : fund.return_2yr < 0 ? 'negative' : ''}>
                        {formatPercentDirect(fund.return_2yr)}
                      </td>
                      <td className={fund.return_3yr > 0 ? 'positive' : fund.return_3yr < 0 ? 'negative' : ''}>
                        {formatPercentDirect(fund.return_3yr)}
                      </td>
                      <td className={fund.return_5yr > 0 ? 'positive' : fund.return_5yr < 0 ? 'negative' : ''}>
                        {formatPercentDirect(fund.return_5yr)}
                      </td>
                      <td className={fund.return_7yr > 0 ? 'positive' : fund.return_7yr < 0 ? 'negative' : ''}>
                        {formatPercentDirect(fund.return_7yr)}
                      </td>
                      <td className={fund.return_10yr > 0 ? 'positive' : fund.return_10yr < 0 ? 'negative' : ''}>
                        {formatPercentDirect(fund.return_10yr)}
                      </td>
                      <td>{formatPercentDirect(fund.vol_3yr)}</td>
                      {isDrummondUser && (
                        <td
                          className={fund.intercept != null ? (fund.intercept > 0 ? 'positive' : fund.intercept < 0 ? 'negative' : '') : ''}
                          title={fund.intercept != null ? `Intercept: ${(fund.intercept * 100).toFixed(2)}% per month. The return not explained by factor exposures — positive means the fund adds value beyond its style/tilt betas.` : ''}
                        >
                          {fund.intercept != null ? (fund.intercept * 100).toFixed(2) + '%' : '-'}
                        </td>
                      )}
                      <td className="negative">{formatPercentDirect(fund.max_drawdown)}</td>
                      <td>{formatPercentDirect(fund.tracking_error)}</td>
                      <td>{formatPercentDirect(fund.min_alpha)}</td>
                      <td>{formatPercentDirect(fund.max_alpha)}</td>
                      <td>{formatPercentDirect(fund.std_dev_alpha)}</td>
                      <td className="streak-cell">
                        {fund.alpha_streak != null ? (
                          <span className={`streak-badge ${
                            fund.alpha_streak <= -6 ? 'streak-danger' :
                            fund.alpha_streak < 0 ? 'streak-warning' :
                            'streak-success'
                          }`}>
                            {fund.alpha_streak > 0 ? '+' : ''}{fund.alpha_streak}m
                          </span>
                        ) : '-'}
                      </td>
                      <td className="pctile-cell">
                        {fund.pctile_3yr != null ? (
                          <span className={`pctile-value ${
                            fund.pctile_3yr >= 75 ? 'pctile-high' :
                            fund.pctile_3yr >= 25 ? 'pctile-mid' :
                            'pctile-low'
                          }`} title={`Rank within ${fund.asset_class} peer group`}>
                            {fund.pctile_3yr}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="trend-cell">
                        {fund.alpha_slope_12m != null ? (
                          <span
                            className={`trend-indicator ${
                              fund.alpha_slope_12m > 0.3 ? 'trend-strong-up' :
                              fund.alpha_slope_12m > 0 ? 'trend-up' :
                              fund.alpha_slope_12m > -0.3 ? 'trend-down' :
                              'trend-strong-down'
                            }`}
                            title={`${fund.alpha_slope_12m.toFixed(2)} bp/month`}
                          >
                            {fund.alpha_slope_12m > 0.3 ? '▲▲' :
                             fund.alpha_slope_12m > 0 ? '▲' :
                             fund.alpha_slope_12m > -0.3 ? '▼' : '▼▼'}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="apir-code">{fund.apir_code || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
        </div>
      )}

      {/* Charts Section */}
      {(selectedFunds.length > 0 || (showAverage && averageChartData)) && (
        <div className="charts-section">
          <h5>Fund Comparison Charts</h5>

          {(isLoadingCharts || isLoadingAverage) ? (
            <div className="loading-spinner">
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Loading charts...</span>
              </div>
            </div>
          ) : (chartData || averageChartData) ? (
            <div className="charts-grid">
              {/* Row 1: 3yr Cumulative Return, 5yr Cumulative Return, 7yr Cumulative Return */}
              {(chartData?.performance_3yr?.length > 0 || averageChartData?.performance_3yr?.length > 0) && buildChartConfig(chartData?.performance_3yr, '3 Year Cumulative Return', true, averageChartData?.performance_3yr) && (
                <div className="chart-container">
                  <button
                    className="chart-expand-btn"
                    onClick={() => openFullscreenChart('line', '3 Year Cumulative Return (%)', buildChartConfig(chartData?.performance_3yr, '3 Year Cumulative Return', true, averageChartData?.performance_3yr), chartOptions('3 Year Cumulative Return (%)'))}
                    title="Expand chart"
                  >
                    <svg viewBox="0 0 16 16" fill="currentColor"><path d="M5.828 10.172a.5.5 0 0 0-.707 0l-4.096 4.096V11.5a.5.5 0 0 0-1 0v3.975a.5.5 0 0 0 .5.5H4.5a.5.5 0 0 0 0-1H1.732l4.096-4.096a.5.5 0 0 0 0-.707zm4.344 0a.5.5 0 0 1 .707 0l4.096 4.096V11.5a.5.5 0 1 1 1 0v3.975a.5.5 0 0 1-.5.5H11.5a.5.5 0 0 1 0-1h2.768l-4.096-4.096a.5.5 0 0 1 0-.707zm0-4.344a.5.5 0 0 0 .707 0l4.096-4.096V4.5a.5.5 0 1 0 1 0V.525a.5.5 0 0 0-.5-.5H11.5a.5.5 0 0 0 0 1h2.768l-4.096 4.096a.5.5 0 0 0 0 .707zm-4.344 0a.5.5 0 0 1-.707 0L1.025 1.732V4.5a.5.5 0 0 1-1 0V.525a.5.5 0 0 1 .5-.5H4.5a.5.5 0 0 1 0 1H1.732l4.096 4.096a.5.5 0 0 1 0 .707z"/></svg> Expand
                  </button>
                  <Line
                    data={buildChartConfig(chartData?.performance_3yr, '3 Year Cumulative Return', true, averageChartData?.performance_3yr)}
                    options={chartOptions('3 Year Cumulative Return (%)')}
                  />
                </div>
              )}

              {(chartData?.performance?.length > 0 || averageChartData?.performance?.length > 0) && buildChartConfig(chartData?.performance, '5 Year Cumulative Return', true, averageChartData?.performance) && (
                <div className="chart-container">
                  <button
                    className="chart-expand-btn"
                    onClick={() => openFullscreenChart('line', '5 Year Cumulative Return (%)', buildChartConfig(chartData?.performance, '5 Year Cumulative Return', true, averageChartData?.performance), chartOptions('5 Year Cumulative Return (%)'))}
                    title="Expand chart"
                  >
                    <svg viewBox="0 0 16 16" fill="currentColor"><path d="M5.828 10.172a.5.5 0 0 0-.707 0l-4.096 4.096V11.5a.5.5 0 0 0-1 0v3.975a.5.5 0 0 0 .5.5H4.5a.5.5 0 0 0 0-1H1.732l4.096-4.096a.5.5 0 0 0 0-.707zm4.344 0a.5.5 0 0 1 .707 0l4.096 4.096V11.5a.5.5 0 1 1 1 0v3.975a.5.5 0 0 1-.5.5H11.5a.5.5 0 0 1 0-1h2.768l-4.096-4.096a.5.5 0 0 1 0-.707zm0-4.344a.5.5 0 0 0 .707 0l4.096-4.096V4.5a.5.5 0 1 0 1 0V.525a.5.5 0 0 0-.5-.5H11.5a.5.5 0 0 0 0 1h2.768l-4.096 4.096a.5.5 0 0 0 0 .707zm-4.344 0a.5.5 0 0 1-.707 0L1.025 1.732V4.5a.5.5 0 0 1-1 0V.525a.5.5 0 0 1 .5-.5H4.5a.5.5 0 0 1 0 1H1.732l4.096 4.096a.5.5 0 0 1 0 .707z"/></svg> Expand
                  </button>
                  <Line
                    data={buildChartConfig(chartData?.performance, '5 Year Cumulative Return', true, averageChartData?.performance)}
                    options={chartOptions('5 Year Cumulative Return (%)')}
                  />
                </div>
              )}

              {(chartData?.performance_7yr?.length > 0 || averageChartData?.performance_7yr?.length > 0) && buildChartConfig(chartData?.performance_7yr, '7 Year Cumulative Return', true, averageChartData?.performance_7yr) && (
                <div className="chart-container">
                  <button
                    className="chart-expand-btn"
                    onClick={() => openFullscreenChart('line', '7 Year Cumulative Return (%)', buildChartConfig(chartData?.performance_7yr, '7 Year Cumulative Return', true, averageChartData?.performance_7yr), chartOptions('7 Year Cumulative Return (%)'))}
                    title="Expand chart"
                  >
                    <svg viewBox="0 0 16 16" fill="currentColor"><path d="M5.828 10.172a.5.5 0 0 0-.707 0l-4.096 4.096V11.5a.5.5 0 0 0-1 0v3.975a.5.5 0 0 0 .5.5H4.5a.5.5 0 0 0 0-1H1.732l4.096-4.096a.5.5 0 0 0 0-.707zm4.344 0a.5.5 0 0 1 .707 0l4.096 4.096V11.5a.5.5 0 1 1 1 0v3.975a.5.5 0 0 1-.5.5H11.5a.5.5 0 0 1 0-1h2.768l-4.096-4.096a.5.5 0 0 1 0-.707zm0-4.344a.5.5 0 0 0 .707 0l4.096-4.096V4.5a.5.5 0 1 0 1 0V.525a.5.5 0 0 0-.5-.5H11.5a.5.5 0 0 0 0 1h2.768l-4.096 4.096a.5.5 0 0 0 0 .707zm-4.344 0a.5.5 0 0 1-.707 0L1.025 1.732V4.5a.5.5 0 0 1-1 0V.525a.5.5 0 0 1 .5-.5H4.5a.5.5 0 0 1 0 1H1.732l4.096 4.096a.5.5 0 0 1 0 .707z"/></svg> Expand
                  </button>
                  <Line
                    data={buildChartConfig(chartData?.performance_7yr, '7 Year Cumulative Return', true, averageChartData?.performance_7yr)}
                    options={chartOptions('7 Year Cumulative Return (%)')}
                  />
                </div>
              )}

              {/* Row 2: Rolling Alpha 6m, Rolling Alpha 1yr, Rolling Alpha 3yr */}
              {(chartData?.rolling_alpha_6m?.length > 0 || averageChartData?.rolling_alpha_6m?.length > 0) && buildChartConfig(chartData?.rolling_alpha_6m, 'Rolling Alpha (6 Month)', true, averageChartData?.rolling_alpha_6m) && (
                <div className="chart-container">
                  <button
                    className="chart-expand-btn"
                    onClick={() => openFullscreenChart('line', 'Rolling Alpha 6m (%)', buildChartConfig(chartData?.rolling_alpha_6m, 'Rolling Alpha (6 Month)', true, averageChartData?.rolling_alpha_6m), chartOptions('Rolling Alpha 6m (%)'))}
                    title="Expand chart"
                  >
                    <svg viewBox="0 0 16 16" fill="currentColor"><path d="M5.828 10.172a.5.5 0 0 0-.707 0l-4.096 4.096V11.5a.5.5 0 0 0-1 0v3.975a.5.5 0 0 0 .5.5H4.5a.5.5 0 0 0 0-1H1.732l4.096-4.096a.5.5 0 0 0 0-.707zm4.344 0a.5.5 0 0 1 .707 0l4.096 4.096V11.5a.5.5 0 1 1 1 0v3.975a.5.5 0 0 1-.5.5H11.5a.5.5 0 0 1 0-1h2.768l-4.096-4.096a.5.5 0 0 1 0-.707zm0-4.344a.5.5 0 0 0 .707 0l4.096-4.096V4.5a.5.5 0 1 0 1 0V.525a.5.5 0 0 0-.5-.5H11.5a.5.5 0 0 0 0 1h2.768l-4.096 4.096a.5.5 0 0 0 0 .707zm-4.344 0a.5.5 0 0 1-.707 0L1.025 1.732V4.5a.5.5 0 0 1-1 0V.525a.5.5 0 0 1 .5-.5H4.5a.5.5 0 0 1 0 1H1.732l4.096 4.096a.5.5 0 0 1 0 .707z"/></svg> Expand
                  </button>
                  <Line
                    data={buildChartConfig(chartData?.rolling_alpha_6m, 'Rolling Alpha (6 Month)', true, averageChartData?.rolling_alpha_6m)}
                    options={chartOptions('Rolling Alpha 6m (%)')}
                  />
                </div>
              )}

              {(chartData?.rolling_alpha?.length > 0 || averageChartData?.rolling_alpha?.length > 0) && buildChartConfig(chartData?.rolling_alpha, 'Rolling Alpha (1 Year)', true, averageChartData?.rolling_alpha) && (
                <div className="chart-container">
                  <button
                    className="chart-expand-btn"
                    onClick={() => openFullscreenChart('line', 'Rolling Alpha 1yr (%)', buildChartConfig(chartData?.rolling_alpha, 'Rolling Alpha (1 Year)', true, averageChartData?.rolling_alpha), chartOptions('Rolling Alpha 1yr (%)'))}
                    title="Expand chart"
                  >
                    <svg viewBox="0 0 16 16" fill="currentColor"><path d="M5.828 10.172a.5.5 0 0 0-.707 0l-4.096 4.096V11.5a.5.5 0 0 0-1 0v3.975a.5.5 0 0 0 .5.5H4.5a.5.5 0 0 0 0-1H1.732l4.096-4.096a.5.5 0 0 0 0-.707zm4.344 0a.5.5 0 0 1 .707 0l4.096 4.096V11.5a.5.5 0 1 1 1 0v3.975a.5.5 0 0 1-.5.5H11.5a.5.5 0 0 1 0-1h2.768l-4.096-4.096a.5.5 0 0 1 0-.707zm0-4.344a.5.5 0 0 0 .707 0l4.096-4.096V4.5a.5.5 0 1 0 1 0V.525a.5.5 0 0 0-.5-.5H11.5a.5.5 0 0 0 0 1h2.768l-4.096 4.096a.5.5 0 0 0 0 .707zm-4.344 0a.5.5 0 0 1-.707 0L1.025 1.732V4.5a.5.5 0 0 1-1 0V.525a.5.5 0 0 1 .5-.5H4.5a.5.5 0 0 1 0 1H1.732l4.096 4.096a.5.5 0 0 1 0 .707z"/></svg> Expand
                  </button>
                  <Line
                    data={buildChartConfig(chartData?.rolling_alpha, 'Rolling Alpha (1 Year)', true, averageChartData?.rolling_alpha)}
                    options={chartOptions('Rolling Alpha 1yr (%)')}
                  />
                </div>
              )}

              {(chartData?.rolling_alpha_3yr?.length > 0 || averageChartData?.rolling_alpha_3yr?.length > 0) && buildChartConfig(chartData?.rolling_alpha_3yr, 'Rolling Alpha (3 Year)', true, averageChartData?.rolling_alpha_3yr) && (
                <div className="chart-container">
                  <button
                    className="chart-expand-btn"
                    onClick={() => openFullscreenChart('line', 'Rolling Alpha 3yr (%)', buildChartConfig(chartData?.rolling_alpha_3yr, 'Rolling Alpha (3 Year)', true, averageChartData?.rolling_alpha_3yr), chartOptions('Rolling Alpha 3yr (%)'))}
                    title="Expand chart"
                  >
                    <svg viewBox="0 0 16 16" fill="currentColor"><path d="M5.828 10.172a.5.5 0 0 0-.707 0l-4.096 4.096V11.5a.5.5 0 0 0-1 0v3.975a.5.5 0 0 0 .5.5H4.5a.5.5 0 0 0 0-1H1.732l4.096-4.096a.5.5 0 0 0 0-.707zm4.344 0a.5.5 0 0 1 .707 0l4.096 4.096V11.5a.5.5 0 1 1 1 0v3.975a.5.5 0 0 1-.5.5H11.5a.5.5 0 0 1 0-1h2.768l-4.096-4.096a.5.5 0 0 1 0-.707zm0-4.344a.5.5 0 0 0 .707 0l4.096-4.096V4.5a.5.5 0 1 0 1 0V.525a.5.5 0 0 0-.5-.5H11.5a.5.5 0 0 0 0 1h2.768l-4.096 4.096a.5.5 0 0 0 0 .707zm-4.344 0a.5.5 0 0 1-.707 0L1.025 1.732V4.5a.5.5 0 0 1-1 0V.525a.5.5 0 0 1 .5-.5H4.5a.5.5 0 0 1 0 1H1.732l4.096 4.096a.5.5 0 0 1 0 .707z"/></svg> Expand
                  </button>
                  <Line
                    data={buildChartConfig(chartData?.rolling_alpha_3yr, 'Rolling Alpha (3 Year)', true, averageChartData?.rolling_alpha_3yr)}
                    options={chartOptions('Rolling Alpha 3yr (%)')}
                  />
                </div>
              )}

              {/* Row 3: Since Inception, Drawdown, Cumulative Alpha */}
              {chartData?.since_inception?.length > 0 && (
                <div className="chart-container">
                  <button
                    className="chart-expand-btn"
                    onClick={() => openFullscreenChart('line', 'Since Inception Returns (%)', buildChartConfig(chartData.since_inception, 'Since Inception Returns'), chartOptions('Since Inception Returns (%)'))}
                    title="Expand chart"
                  >
                    <svg viewBox="0 0 16 16" fill="currentColor"><path d="M5.828 10.172a.5.5 0 0 0-.707 0l-4.096 4.096V11.5a.5.5 0 0 0-1 0v3.975a.5.5 0 0 0 .5.5H4.5a.5.5 0 0 0 0-1H1.732l4.096-4.096a.5.5 0 0 0 0-.707zm4.344 0a.5.5 0 0 1 .707 0l4.096 4.096V11.5a.5.5 0 1 1 1 0v3.975a.5.5 0 0 1-.5.5H11.5a.5.5 0 0 1 0-1h2.768l-4.096-4.096a.5.5 0 0 1 0-.707zm0-4.344a.5.5 0 0 0 .707 0l4.096-4.096V4.5a.5.5 0 1 0 1 0V.525a.5.5 0 0 0-.5-.5H11.5a.5.5 0 0 0 0 1h2.768l-4.096 4.096a.5.5 0 0 0 0 .707zm-4.344 0a.5.5 0 0 1-.707 0L1.025 1.732V4.5a.5.5 0 0 1-1 0V.525a.5.5 0 0 1 .5-.5H4.5a.5.5 0 0 1 0 1H1.732l4.096 4.096a.5.5 0 0 1 0 .707z"/></svg> Expand
                  </button>
                  <Line
                    data={buildChartConfig(chartData.since_inception, 'Since Inception Returns')}
                    options={chartOptions('Since Inception Returns (%)')}
                  />
                </div>
              )}

              {(chartData?.drawdown?.length > 0 || averageChartData?.drawdown?.length > 0) && buildChartConfig(chartData?.drawdown, 'Drawdown (%)', true, averageChartData?.drawdown) && (
                <div className="chart-container">
                  <button
                    className="chart-expand-btn"
                    onClick={() => openFullscreenChart('line', 'Drawdown (%)', buildChartConfig(chartData?.drawdown, 'Drawdown (%)', true, averageChartData?.drawdown), chartOptions('Drawdown (%)'))}
                    title="Expand chart"
                  >
                    <svg viewBox="0 0 16 16" fill="currentColor"><path d="M5.828 10.172a.5.5 0 0 0-.707 0l-4.096 4.096V11.5a.5.5 0 0 0-1 0v3.975a.5.5 0 0 0 .5.5H4.5a.5.5 0 0 0 0-1H1.732l4.096-4.096a.5.5 0 0 0 0-.707zm4.344 0a.5.5 0 0 1 .707 0l4.096 4.096V11.5a.5.5 0 1 1 1 0v3.975a.5.5 0 0 1-.5.5H11.5a.5.5 0 0 1 0-1h2.768l-4.096-4.096a.5.5 0 0 1 0-.707zm0-4.344a.5.5 0 0 0 .707 0l4.096-4.096V4.5a.5.5 0 1 0 1 0V.525a.5.5 0 0 0-.5-.5H11.5a.5.5 0 0 0 0 1h2.768l-4.096 4.096a.5.5 0 0 0 0 .707zm-4.344 0a.5.5 0 0 1-.707 0L1.025 1.732V4.5a.5.5 0 0 1-1 0V.525a.5.5 0 0 1 .5-.5H4.5a.5.5 0 0 1 0 1H1.732l4.096 4.096a.5.5 0 0 1 0 .707z"/></svg> Expand
                  </button>
                  <Line
                    data={buildChartConfig(chartData?.drawdown, 'Drawdown (%)', true, averageChartData?.drawdown)}
                    options={chartOptions('Drawdown (%)')}
                  />
                </div>
              )}

              {(chartData?.alpha_drawdown?.length > 0 || averageChartData?.alpha_drawdown?.length > 0) && buildChartConfig(chartData?.alpha_drawdown, 'Alpha Drawdown', true, averageChartData?.alpha_drawdown) && (
                <div className="chart-container">
                  <button
                    className="chart-expand-btn"
                    onClick={() => openFullscreenChart('line', 'Alpha Drawdown (%)', buildChartConfig(chartData?.alpha_drawdown, 'Alpha Drawdown', true, averageChartData?.alpha_drawdown), chartOptions('Alpha Drawdown (Distance from Peak Alpha)'))}
                    title="Expand chart"
                  >
                    <svg viewBox="0 0 16 16" fill="currentColor"><path d="M5.828 10.172a.5.5 0 0 0-.707 0l-4.096 4.096V11.5a.5.5 0 0 0-1 0v3.975a.5.5 0 0 0 .5.5H4.5a.5.5 0 0 0 0-1H1.732l4.096-4.096a.5.5 0 0 0 0-.707zm4.344 0a.5.5 0 0 1 .707 0l4.096 4.096V11.5a.5.5 0 1 1 1 0v3.975a.5.5 0 0 1-.5.5H11.5a.5.5 0 0 1 0-1h2.768l-4.096-4.096a.5.5 0 0 1 0-.707zm0-4.344a.5.5 0 0 0 .707 0l4.096-4.096V4.5a.5.5 0 1 0 1 0V.525a.5.5 0 0 0-.5-.5H11.5a.5.5 0 0 0 0 1h2.768l-4.096 4.096a.5.5 0 0 0 0 .707zm-4.344 0a.5.5 0 0 1-.707 0L1.025 1.732V4.5a.5.5 0 0 1-1 0V.525a.5.5 0 0 1 .5-.5H4.5a.5.5 0 0 1 0 1H1.732l4.096 4.096a.5.5 0 0 1 0 .707z"/></svg> Expand
                  </button>
                  <Line
                    data={buildChartConfig(chartData?.alpha_drawdown, 'Alpha Drawdown', true, averageChartData?.alpha_drawdown)}
                    options={chartOptions('Alpha Drawdown (Distance from Peak Alpha)')}
                  />
                </div>
              )}

              {(chartData?.cumulative_alpha?.length > 0 || averageChartData?.cumulative_alpha?.length > 0) && buildChartConfig(chartData?.cumulative_alpha, 'Cumulative Alpha', true, averageChartData?.cumulative_alpha) && (
                <div className="chart-container">
                  <button
                    className="chart-expand-btn"
                    onClick={() => openFullscreenChart('line', 'Cumulative Alpha (%)', buildChartConfig(chartData?.cumulative_alpha, 'Cumulative Alpha', true, averageChartData?.cumulative_alpha), chartOptions('Cumulative Alpha (%)'))}
                    title="Expand chart"
                  >
                    <svg viewBox="0 0 16 16" fill="currentColor"><path d="M5.828 10.172a.5.5 0 0 0-.707 0l-4.096 4.096V11.5a.5.5 0 0 0-1 0v3.975a.5.5 0 0 0 .5.5H4.5a.5.5 0 0 0 0-1H1.732l4.096-4.096a.5.5 0 0 0 0-.707zm4.344 0a.5.5 0 0 1 .707 0l4.096 4.096V11.5a.5.5 0 1 1 1 0v3.975a.5.5 0 0 1-.5.5H11.5a.5.5 0 0 1 0-1h2.768l-4.096-4.096a.5.5 0 0 1 0-.707zm0-4.344a.5.5 0 0 0 .707 0l4.096-4.096V4.5a.5.5 0 1 0 1 0V.525a.5.5 0 0 0-.5-.5H11.5a.5.5 0 0 0 0 1h2.768l-4.096 4.096a.5.5 0 0 0 0 .707zm-4.344 0a.5.5 0 0 1-.707 0L1.025 1.732V4.5a.5.5 0 0 1-1 0V.525a.5.5 0 0 1 .5-.5H4.5a.5.5 0 0 1 0 1H1.732l4.096 4.096a.5.5 0 0 1 0 .707z"/></svg> Expand
                  </button>
                  <Line
                    data={buildChartConfig(chartData?.cumulative_alpha, 'Cumulative Alpha', true, averageChartData?.cumulative_alpha)}
                    options={chartOptions('Cumulative Alpha (%)')}
                  />
                </div>
              )}

              {/* Row 4: Calendar Returns, Risk/Reward, Correlation Matrix */}
              {chartData?.calendar_returns?.length > 0 && buildCalendarReturnsConfig(chartData.calendar_returns) && (
                <div className="chart-container">
                  <button
                    className="chart-expand-btn"
                    onClick={() => openFullscreenChart('bar', 'Calendar Year Returns', buildCalendarReturnsConfig(chartData.calendar_returns), calendarChartOptions)}
                    title="Expand chart"
                  >
                    <svg viewBox="0 0 16 16" fill="currentColor"><path d="M5.828 10.172a.5.5 0 0 0-.707 0l-4.096 4.096V11.5a.5.5 0 0 0-1 0v3.975a.5.5 0 0 0 .5.5H4.5a.5.5 0 0 0 0-1H1.732l4.096-4.096a.5.5 0 0 0 0-.707zm4.344 0a.5.5 0 0 1 .707 0l4.096 4.096V11.5a.5.5 0 1 1 1 0v3.975a.5.5 0 0 1-.5.5H11.5a.5.5 0 0 1 0-1h2.768l-4.096-4.096a.5.5 0 0 1 0-.707zm0-4.344a.5.5 0 0 0 .707 0l4.096-4.096V4.5a.5.5 0 1 0 1 0V.525a.5.5 0 0 0-.5-.5H11.5a.5.5 0 0 0 0 1h2.768l-4.096 4.096a.5.5 0 0 0 0 .707zm-4.344 0a.5.5 0 0 1-.707 0L1.025 1.732V4.5a.5.5 0 0 1-1 0V.525a.5.5 0 0 1 .5-.5H4.5a.5.5 0 0 1 0 1H1.732l4.096 4.096a.5.5 0 0 1 0 .707z"/></svg> Expand
                  </button>
                  <Bar
                    data={buildCalendarReturnsConfig(chartData.calendar_returns)}
                    options={calendarChartOptions}
                  />
                </div>
              )}

              {chartData?.risk_reward?.length > 0 && buildRiskRewardConfig(chartData.risk_reward) && (
                <div className="chart-container">
                  <button
                    className="chart-expand-btn"
                    onClick={() => openFullscreenChart('scatter', 'Risk/Reward - 3 Years', buildRiskRewardConfig(chartData.risk_reward), riskRewardChartOptions)}
                    title="Expand chart"
                  >
                    <svg viewBox="0 0 16 16" fill="currentColor"><path d="M5.828 10.172a.5.5 0 0 0-.707 0l-4.096 4.096V11.5a.5.5 0 0 0-1 0v3.975a.5.5 0 0 0 .5.5H4.5a.5.5 0 0 0 0-1H1.732l4.096-4.096a.5.5 0 0 0 0-.707zm4.344 0a.5.5 0 0 1 .707 0l4.096 4.096V11.5a.5.5 0 1 1 1 0v3.975a.5.5 0 0 1-.5.5H11.5a.5.5 0 0 1 0-1h2.768l-4.096-4.096a.5.5 0 0 1 0-.707zm0-4.344a.5.5 0 0 0 .707 0l4.096-4.096V4.5a.5.5 0 1 0 1 0V.525a.5.5 0 0 0-.5-.5H11.5a.5.5 0 0 0 0 1h2.768l-4.096 4.096a.5.5 0 0 0 0 .707zm-4.344 0a.5.5 0 0 1-.707 0L1.025 1.732V4.5a.5.5 0 0 1-1 0V.525a.5.5 0 0 1 .5-.5H4.5a.5.5 0 0 1 0 1H1.732l4.096 4.096a.5.5 0 0 1 0 .707z"/></svg> Expand
                  </button>
                  <Scatter
                    data={buildRiskRewardConfig(chartData.risk_reward)}
                    options={riskRewardChartOptions}
                  />
                </div>
              )}

              {/* Correlation Matrix + Factor Loadings side by side */}
              {(chartData?.correlation_matrix?.length > 0 || (factorData?.sac_groups && Object.keys(factorData.sac_groups).length > 0)) && (
                <div className="corr-factor-row">
                  {/* Correlation Matrix Heatmap */}
                  {chartData?.correlation_matrix?.length > 0 && (
                    <div className="chart-container correlation-container">
                      <h6 className="correlation-title">Correlation Matrix</h6>
                      <div className="correlation-matrix">
                        {(() => {
                          // Build unique fund list
                          const funds = new Map();
                          chartData.correlation_matrix.forEach(entry => {
                            funds.set(entry.fund1_tr_code, entry.fund1_name);
                            funds.set(entry.fund2_tr_code, entry.fund2_name);
                          });
                          const fundList = Array.from(funds.entries());

                          // Build correlation lookup
                          const correlations = new Map();
                          chartData.correlation_matrix.forEach(entry => {
                            correlations.set(`${entry.fund1_tr_code}-${entry.fund2_tr_code}`, entry.correlation);
                            correlations.set(`${entry.fund2_tr_code}-${entry.fund1_tr_code}`, entry.correlation);
                          });

                          return (
                            <table className="correlation-table">
                              <thead>
                                <tr>
                                  <th></th>
                                  {fundList.map(([code, name]) => (
                                    <th key={code} title={name}>
                                      {name.length > 15 ? name.substring(0, 15) + '...' : name}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {fundList.map(([rowCode, rowName]) => (
                                  <tr key={rowCode}>
                                    <td className="row-header" title={rowName}>
                                      {rowName.length > 15 ? rowName.substring(0, 15) + '...' : rowName}
                                    </td>
                                    {fundList.map(([colCode]) => {
                                      if (rowCode === colCode) {
                                        return (
                                          <td key={colCode} className="correlation-cell" style={{ backgroundColor: '#91CF9B' }}>
                                            1.00
                                          </td>
                                        );
                                      }
                                      const corr = correlations.get(`${rowCode}-${colCode}`);
                                      return (
                                        <td
                                          key={colCode}
                                          className="correlation-cell"
                                          style={{ backgroundColor: corr != null ? getCorrelationColor(corr) : '#f0f0f0' }}
                                        >
                                          {corr != null ? corr.toFixed(2) : '-'}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Factor Loadings Table */}
                  {isDrummondUser && factorData?.sac_groups && Object.keys(factorData.sac_groups).length > 0 && (
                    <div className="factor-loading-container">
                  <h6 className="correlation-title">Factor Loadings</h6>
                  {isLoadingFactors ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    Object.entries(factorData.sac_groups).map(([sac, funds]) => {
                      const factors = factorData.sac_factors?.[sac] || [];
                      const sacLabel = sac === 'EQ_AU' ? 'Australian Equities'
                        : sac === 'EQ_WORLD' ? 'International Equities'
                        : sac === 'EQ_INTL' ? 'International Equities'
                        : sac === 'EQ_EM' ? 'EM Equities'
                        : sac;

                      return (
                        <div key={sac} className="factor-sac-group">
                          <h6 className="factor-sac-title">{sacLabel}</h6>
                          <div style={{ overflowX: 'auto' }}>
                            <table className="factor-table">
                              <thead>
                                <tr>
                                  <th>Fund</th>
                                  <th title="Portfolio weight for this fund. Edit to see weighted portfolio factor profile in the total row.">Wt %</th>
                                  <th title="R-squared — proportion of return variation explained by the factor model. Higher = more reliable betas.">R²</th>
                                  {factors.map(f => (
                                    <th key={f} title={`Beta to ${f.replace(/_/g, ' ')} factor. Measures the fund's sensitivity to this factor — higher absolute value = stronger exposure.`}>{abbreviateFactorName(f)}</th>
                                  ))}
                                  <th title="Annualised specific (idiosyncratic) risk — volatility not explained by factor exposures.">Spec Risk</th>
                                  <th title="Annualised factor risk — volatility attributable to the fund's factor exposures.">Factor Risk</th>
                                  <th title="Total annualised risk = sqrt(Factor Risk² + Specific Risk²).">Total Risk</th>
                                </tr>
                              </thead>
                              <tbody>
                                {funds.map(fund => (
                                  <tr key={fund.tr_code}>
                                    <td className="factor-fund-name" title={fund.fund_name}>
                                      {fund.fund_name.length > 25 ? fund.fund_name.substring(0, 25) + '...' : fund.fund_name}
                                    </td>
                                    <td>
                                      <input
                                        type="number"
                                        className="factor-weight-input"
                                        value={factorWeights[fund.tr_code] ?? ''}
                                        onChange={(e) => handleFactorWeightChange(fund.tr_code, e.target.value)}
                                        min="0"
                                        max="100"
                                        step="1"
                                      />
                                    </td>
                                    <td>{fund.r_squared != null ? fund.r_squared.toFixed(2) : '-'}</td>
                                    {factors.map(f => (
                                      <td
                                        key={f}
                                        className="factor-beta-cell"
                                        style={{ backgroundColor: getBetaColor(fund.betas?.[f]) }}
                                      >
                                        {fund.betas?.[f] != null ? fund.betas[f].toFixed(2) : '-'}
                                      </td>
                                    ))}
                                    <td>{fund.specific_risk_annual != null ? fund.specific_risk_annual.toFixed(2) : '-'}</td>
                                    <td>{fund.factor_risk_annual != null ? fund.factor_risk_annual.toFixed(2) : '-'}</td>
                                    <td>{fund.total_risk_annual != null ? fund.total_risk_annual.toFixed(2) : '-'}</td>
                                  </tr>
                                ))}
                                {/* Portfolio weighted total row */}
                                {funds.length > 1 && (() => {
                                  const totalWeight = funds.reduce((sum, f) => sum + (factorWeights[f.tr_code] || 0), 0);
                                  return (
                                    <tr className="factor-portfolio-row">
                                      <td><strong>Portfolio</strong></td>
                                      <td><strong>{totalWeight.toFixed(0)}%</strong></td>
                                      <td>-</td>
                                      {factors.map(f => {
                                        const weightedBeta = funds.reduce((sum, fund) => {
                                          const w = (factorWeights[fund.tr_code] || 0) / 100;
                                          const beta = fund.betas?.[f];
                                          return sum + (beta != null ? w * beta : 0);
                                        }, 0);
                                        return (
                                          <td
                                            key={f}
                                            className="factor-beta-cell"
                                            style={{ backgroundColor: getBetaColor(weightedBeta) }}
                                          >
                                            {weightedBeta.toFixed(2)}
                                          </td>
                                        );
                                      })}
                                      <td>-</td>
                                      <td>-</td>
                                      <td>-</td>
                                    </tr>
                                  );
                                })()}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })
                  )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <p className="text-muted">No chart data available for selected funds</p>
          )}
        </div>
      )}

      {/* Loading Alpha Modal */}
      {isLoadingAlpha && (
        <div className="alpha-modal-overlay">
          <div className="alpha-modal">
            <div className="alpha-modal-header">
              <h5>Loading Monthly Alpha...</h5>
            </div>
            <div className="alpha-modal-body" style={{ textAlign: 'center', padding: '40px' }}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Discrete Monthly Alpha Modal */}
      {alphaModalFund && !isLoadingAlpha && (
        <div className="alpha-modal-overlay" onClick={() => setAlphaModalFund(null)}>
          <div className="alpha-modal" onClick={(e) => e.stopPropagation()}>
            <div className="alpha-modal-header">
              <h5>Discrete Monthly Alpha - {alphaModalFund.fund_name}</h5>
              <button className="alpha-modal-close" onClick={() => setAlphaModalFund(null)}>
                &times;
              </button>
            </div>
            <div className="alpha-modal-body">
              <p style={{ fontSize: '11px', color: '#6c757d', fontStyle: 'italic', margin: '0 0 10px 0' }}>
                Alpha is measured against a Drummond-assigned representative passive fund/ETF for the sub-asset class, which may differ from the fund's stated benchmark.
              </p>
              {(() => {
                const grid = calculateDiscreteAlpha(alphaModalFund.data);
                const years = Object.keys(grid).sort().reverse(); // Most recent first
                const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

                return (
                  <div className="alpha-fund-table">
                    <div className="alpha-table-wrapper">
                      <table className="alpha-table">
                        <thead>
                          <tr>
                            <th>Year</th>
                            {months.map(m => <th key={m}>{m}</th>)}
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {years.map(year => {
                            // Calculate year total
                            let yearTotal = 0;
                            let hasAnyValue = false;
                            [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].forEach(month => {
                              const value = grid[year]?.[month];
                              if (value !== undefined && value !== null) {
                                yearTotal += value;
                                hasAnyValue = true;
                              }
                            });

                            return (
                              <tr key={year}>
                                <td className="alpha-year-cell">{year}</td>
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => {
                                  const value = grid[year]?.[month];
                                  const hasValue = value !== undefined && value !== null;
                                  const isPositive = hasValue && value > 0;
                                  const isNegative = hasValue && value < 0;
                                  return (
                                    <td
                                      key={month}
                                      className={`alpha-cell ${isPositive ? 'positive' : ''} ${isNegative ? 'negative' : ''}`}
                                      style={{
                                        backgroundColor: hasValue
                                          ? isPositive ? 'rgba(145, 207, 155, 0.3)' : 'rgba(231, 76, 60, 0.3)'
                                          : 'transparent'
                                      }}
                                    >
                                      {hasValue ? value.toFixed(2) : '-'}
                                    </td>
                                  );
                                })}
                                <td
                                  className={`alpha-cell alpha-total ${yearTotal > 0 ? 'positive' : ''} ${yearTotal < 0 ? 'negative' : ''}`}
                                  style={{
                                    backgroundColor: hasAnyValue
                                      ? yearTotal > 0 ? 'rgba(145, 207, 155, 0.5)' : 'rgba(231, 76, 60, 0.5)'
                                      : 'transparent',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  {hasAnyValue ? yearTotal.toFixed(2) : '-'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="alpha-modal-footer">
              <button className="btn btn-secondary" onClick={() => setAlphaModalFund(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Returns Modal */}
      {returnsModalFund && (
        <div className="returns-modal-overlay" onClick={closeReturnsModal}>
          <div className="returns-modal" onClick={(e) => e.stopPropagation()}>
            <div className="returns-modal-header">
              <h5>Point-in-Time Returns - {returnsModalFund.fund_name}</h5>
              <button className="returns-modal-close" onClick={closeReturnsModal}>
                &times;
              </button>
            </div>
            <div className="returns-modal-body">
              <p style={{ fontSize: '11px', color: '#6c757d', fontStyle: 'italic', margin: '0 0 10px 0' }}>
                Returns are calculated from geometrically compounded LSEG monthly data (+ historical track records where available). Alpha is measured against a Drummond-assigned representative passive fund/ETF for the sub-asset class, which may differ from the fund's stated benchmark.
              </p>
              <div className="returns-date-selector">
                <label>Select Date:</label>
                <select value={returnsSelectedDate} onChange={handleReturnsDateChange}>
                  {returnsAvailableDates.map(date => (
                    <option key={date} value={date}>{date}</option>
                  ))}
                </select>
                {isLoadingReturns && <span className="text-muted">Loading...</span>}
              </div>

              {returnsModalData && (
                <div className="returns-table-container">
                  <table className="returns-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>1m</th>
                        <th>3m</th>
                        <th>6m</th>
                        <th>1yr</th>
                        <th>2yr</th>
                        <th>3yr</th>
                        <th>5yr</th>
                        <th>7yr</th>
                        <th>10yr</th>
                        <th>SI</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="return-row">
                        <td>Return</td>
                        {['1m', '3m', '6m', '1yr', '2yr', '3yr', '5yr', '7yr', '10yr', 'si'].map(period => {
                          const val = returnsModalData.returns?.[period];
                          return (
                            <td key={period} className={val != null ? (val > 0 ? 'positive' : val < 0 ? 'negative' : '') : ''}>
                              {val != null ? `${val.toFixed(2)}%` : '-'}
                            </td>
                          );
                        })}
                      </tr>
                      <tr className="alpha-row">
                        <td>Alpha</td>
                        {['1m', '3m', '6m', '1yr', '2yr', '3yr', '5yr', '7yr', '10yr', 'si'].map(period => {
                          const val = returnsModalData.alpha?.[period];
                          return (
                            <td key={period} className={val != null ? (val > 0 ? 'positive' : val < 0 ? 'negative' : '') : ''}>
                              {val != null ? `${val.toFixed(2)}%` : '-'}
                            </td>
                          );
                        })}
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="returns-modal-footer">
              <span className="inception-info">
                {returnsModalData?.inception_date && `Inception: ${returnsModalData.inception_date}`}
              </span>
              <button className="btn btn-secondary" onClick={closeReturnsModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Chart Modal */}
      {fullscreenChart && (
        <div className="fullscreen-chart-overlay" onClick={() => setFullscreenChart(null)}>
          <div className="fullscreen-chart-modal" onClick={(e) => e.stopPropagation()}>
            <div className="fullscreen-chart-header">
              <h5>{fullscreenChart.title}</h5>
              <div className="fullscreen-chart-controls">
                <button className="btn btn-sm btn-outline-secondary me-2" onClick={resetZoom} title="Reset Zoom">
                  <svg viewBox="0 0 16 16" fill="currentColor" style={{width: '14px', height: '14px', marginRight: '4px'}}><path fillRule="evenodd" d="M.172 15.828a.5.5 0 0 0 .707 0l4.096-4.096V14.5a.5.5 0 1 0 1 0v-3.975a.5.5 0 0 0-.5-.5H1.5a.5.5 0 0 0 0 1h2.768L.172 15.121a.5.5 0 0 0 0 .707zM15.828.172a.5.5 0 0 0-.707 0l-4.096 4.096V1.5a.5.5 0 1 0-1 0v3.975a.5.5 0 0 0 .5.5H14.5a.5.5 0 0 0 0-1h-2.768L15.828.879a.5.5 0 0 0 0-.707z"/></svg> Reset Zoom
                </button>
                <button className="fullscreen-chart-close" onClick={() => setFullscreenChart(null)}>
                  &times;
                </button>
              </div>
            </div>
            <div className="fullscreen-chart-body">
              <div className="fullscreen-chart-instructions">
                <small>Scroll to zoom • Drag to pan • Double-click to reset</small>
              </div>
              <div className="fullscreen-chart-container">
                {fullscreenChart.type === 'line' && (
                  <Line ref={fullscreenChartRef} data={fullscreenChart.data} options={fullscreenChart.options} />
                )}
                {fullscreenChart.type === 'bar' && (
                  <Bar ref={fullscreenChartRef} data={fullscreenChart.data} options={fullscreenChart.options} />
                )}
                {fullscreenChart.type === 'scatter' && (
                  <Scatter ref={fullscreenChartRef} data={fullscreenChart.data} options={fullscreenChart.options} />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manager Heatmap Modal */}
      {heatmapModalOpen && (
        <div className="heatmap-modal-overlay" onClick={closeHeatmapModal}>
          <div className="heatmap-modal" onClick={(e) => e.stopPropagation()}>
            <div className="heatmap-modal-header">
              <h5>Performance Heatmap by Asset Class & Style</h5>
              <button className="heatmap-modal-close" onClick={closeHeatmapModal}>
                &times;
              </button>
            </div>
            <div className="heatmap-modal-body">
              {isLoadingHeatmap ? (
                <div className="loading-spinner" style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : heatmapData && heatmapData.length > 0 ? (
                <>
                  <div className="asset-class-buttons-section">
                    <div className="asset-class-buttons-header">
                      <span className="asset-class-buttons-label">Select Asset Classes:</span>
                      {heatmapSelectedAssetClasses.length > 0 && (
                        <button
                          className="asset-class-clear-btn"
                          onClick={() => setHeatmapSelectedAssetClasses([])}
                        >
                          Clear All
                        </button>
                      )}
                    </div>
                    <div className="asset-class-buttons-grid">
                      {heatmapAssetClasses.map(ac => (
                        <button
                          key={ac}
                          className={`asset-class-btn ${heatmapSelectedAssetClasses.includes(ac) ? 'selected' : ''}`}
                          onClick={() => toggleHeatmapAssetClass(ac)}
                        >
                          {ac}
                        </button>
                      ))}
                    </div>
                    {heatmapSelectedAssetClasses.length > 0 && (
                      <div className="filter-results-info">
                        Showing {filteredHeatmapData.length} of {heatmapData.length} rows
                      </div>
                    )}
                  </div>
                  {heatmapSelectedAssetClasses.length === 0 ? (
                    <div className="empty-state-message">
                      <p>Select one or more asset classes above to view performance data.</p>
                    </div>
                  ) : (
                    <div className="heatmap-table-wrapper">
                      <table className="heatmap-table">
                        <thead>
                          <tr>
                            <th>Asset Class</th>
                            <th>Style</th>
                            <th>MTD</th>
                            <th>1m</th>
                            <th>3m</th>
                            <th>6m</th>
                            <th>1yr</th>
                            <th>2yr</th>
                            <th>3yr</th>
                            <th>5yr</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredHeatmapData.map((row, index) => (
                            <tr key={index}>
                              <td className="asset-class-cell">{row.asset_class}</td>
                              <td className="style-cell">{row.style}</td>
                              {HEATMAP_VALUE_KEYS.map((key) => (
                                <td key={key} className="heatmap-value-cell" style={{ backgroundColor: getHeatmapColor(row[key], key) }}>
                                  {row[key] != null ? `${row[key].toFixed(2)}%` : '-'}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-muted" style={{ textAlign: 'center', padding: '20px' }}>No heatmap data available</p>
              )}
            </div>
            <div className="heatmap-modal-footer">
              <div className="heatmap-legend">
                <span className="legend-label">Negative</span>
                <span className="legend-gradient" style={{ background: 'linear-gradient(to right, rgb(178,40,38), rgb(250,216,212), rgb(247,247,247), rgb(220,242,220), rgb(48,158,74))' }}></span>
                <span className="legend-label">Positive</span>
              </div>
              <p style={{ fontSize: '0.75rem', color: '#6c757d', margin: '8px 0 4px', textAlign: 'center' }}>
                Represents average performance of funds within different peer group classifications as assigned by Drummond Capital Partners.
              </p>
              <button className="btn btn-secondary" onClick={closeHeatmapModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Style/Tilt Returns Modal */}
      {styleReturnsModalOpen && (
        <div className="heatmap-modal-overlay" onClick={closeStyleReturnsModal}>
          <div className="heatmap-modal" onClick={(e) => e.stopPropagation()}>
            <div className="heatmap-modal-header">
              <h5>{styleReturnsGroupBy === 'tilt' ? 'Tilt' : 'Style'} Returns{styleReturnsAssetClass ? ` — ${styleReturnsAssetClass}` : ''}</h5>
              <button className="heatmap-modal-close" onClick={closeStyleReturnsModal}>&times;</button>
            </div>
            <div className="heatmap-modal-body">
              {/* Group by toggle — Style vs Tilt */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#072040' }}>Group by:</span>
                <div style={{ display: 'inline-flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid #072040' }}>
                  <button
                    style={{
                      padding: '4px 16px', fontSize: '0.8rem', fontWeight: 600, border: 'none', cursor: 'pointer',
                      backgroundColor: styleReturnsGroupBy === 'style' ? '#072040' : '#fff',
                      color: styleReturnsGroupBy === 'style' ? '#fff' : '#072040',
                    }}
                    onClick={() => handleGroupByChange('style')}
                  >
                    Style
                  </button>
                  <button
                    style={{
                      padding: '4px 16px', fontSize: '0.8rem', fontWeight: 600, border: 'none', cursor: 'pointer',
                      borderLeft: '1px solid #072040',
                      backgroundColor: styleReturnsGroupBy === 'tilt' ? '#072040' : '#fff',
                      color: styleReturnsGroupBy === 'tilt' ? '#fff' : '#072040',
                    }}
                    onClick={() => handleGroupByChange('tilt')}
                  >
                    Tilt
                  </button>
                </div>
              </div>

              {/* Asset class buttons — single-select */}
              <div className="asset-class-buttons-section">
                <div className="asset-class-buttons-header">
                  <span className="asset-class-buttons-label">Select Asset Class:</span>
                </div>
                <div className="asset-class-buttons-grid">
                  {(filterOptions?.asset_class || []).map(ac => (
                    <button
                      key={ac}
                      className={`asset-class-btn ${styleReturnsAssetClass === ac ? 'selected' : ''}`}
                      onClick={() => loadStyleReturns(ac)}
                    >
                      {ac}
                    </button>
                  ))}
                </div>
              </div>

              {/* Content area */}
              {isLoadingStyleReturns ? (
                <div className="loading-spinner" style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : !styleReturnsAssetClass ? (
                <div className="empty-state-message">
                  <p>Select an asset class above to view {styleReturnsGroupBy === 'tilt' ? 'tilt' : 'style'} returns.</p>
                </div>
              ) : styleReturnsData && styleReturnsData.length > 0 ? (
                <>
                  {/* Group checkboxes */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', margin: '16px 0' }}>
                    {styleReturnsData.map((s, idx) => (
                      <label key={s.label} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={styleReturnsSelectedStyles.includes(s.label)}
                          onChange={() => toggleStyleReturnsStyle(s.label)}
                        />
                        <span style={{
                          color: CHART_COLORS[idx % CHART_COLORS.length],
                          fontWeight: 600,
                          ...(idx >= CHART_COLORS.length ? { textDecoration: 'underline', textDecorationStyle: 'dashed' } : {}),
                        }}>
                          {s.label} ({s.fund_count})
                        </span>
                      </label>
                    ))}
                  </div>

                  {/* Charts grid — 4 charts in 2×2 layout */}
                  {styleReturnsSelectedStyles.length > 0 && (() => {
                    const selectedData = styleReturnsData.filter(s => styleReturnsSelectedStyles.includes(s.label));

                    const buildStyleChart = (seriesKey, title) => {
                      const allDates = [...new Set(selectedData.flatMap(s => (s[seriesKey] || []).map(d => d.date)))].sort();
                      if (allDates.length === 0) return null;
                      const DASH_PATTERNS = [[], [6, 3], [2, 2], [8, 4, 2, 4]];
                      const datasets = selectedData.map((s) => {
                        const dateMap = new Map((s[seriesKey] || []).map(d => [d.date, d.value]));
                        const colorIdx = styleReturnsData.findIndex(x => x.label === s.label);
                        const cycle = Math.floor(colorIdx / CHART_COLORS.length);
                        return {
                          label: s.label,
                          data: allDates.map(date => dateMap.get(date) ?? null),
                          borderColor: CHART_COLORS[colorIdx % CHART_COLORS.length],
                          backgroundColor: CHART_COLORS[colorIdx % CHART_COLORS.length],
                          borderWidth: 2,
                          borderDash: DASH_PATTERNS[cycle % DASH_PATTERNS.length],
                          pointRadius: 0,
                          tension: 0.1,
                          spanGaps: true,
                        };
                      });
                      return {
                        data: { labels: allDates, datasets },
                        options: {
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            title: { display: true, text: title, font: { size: 12, weight: 'bold' } },
                            legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 8, font: { size: 9 } } },
                            tooltip: {
                              mode: 'index',
                              intersect: false,
                              callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y?.toFixed(2)}%` },
                            },
                            zoom: { zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'x' }, pan: { enabled: true, mode: 'x' } },
                          },
                          scales: {
                            x: { ticks: { maxTicksLimit: 8, font: { size: 9 } } },
                            y: { ticks: { callback: (v) => `${v.toFixed(0)}%`, font: { size: 9 } } },
                          },
                          interaction: { mode: 'index', intersect: false },
                        },
                      };
                    };

                    const charts = [
                      buildStyleChart('since_inception', 'Since Inception Returns (%)'),
                      buildStyleChart('drawdown', 'Drawdown (%)'),
                      buildStyleChart('alpha_drawdown', 'Alpha Drawdown (Distance from Peak Alpha)'),
                      buildStyleChart('cumulative_alpha', 'Cumulative Alpha (%)'),
                    ].filter(Boolean);

                    return (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '12px' }}>
                        {charts.map((chart, i) => (
                          <div key={i} style={{ height: '300px' }}>
                            <Line data={chart.data} options={chart.options} />
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </>
              ) : styleReturnsData && styleReturnsData.length === 0 ? (
                <p className="text-muted" style={{ textAlign: 'center', padding: '20px' }}>
                  No {styleReturnsGroupBy === 'tilt' ? 'tilt' : 'style'} data available for this asset class.
                </p>
              ) : null}
            </div>
            <div className="heatmap-modal-footer">
              <p style={{ fontSize: '0.75rem', color: '#6c757d', margin: '4px 0', textAlign: 'center' }}>
                Shows averaged time series for flagship funds grouped by {styleReturnsGroupBy === 'tilt' ? 'tilt' : 'style'} classification.
              </p>
              <button className="btn btn-secondary" onClick={closeStyleReturnsModal}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Alpha Regime (Peer Comparison) Modal */}
      {alphaRegimeModalOpen && (
        <div className="alpha-regime-modal-overlay" onClick={closeAlphaRegimeModal}>
          <div className="alpha-regime-modal" onClick={(e) => e.stopPropagation()}>
            <div className="alpha-regime-modal-header">
              <h5>Alpha vs Peer Group{alphaRegimeData ? ` - ${alphaRegimeData.fund_name}` : ''}</h5>
              <button className="alpha-regime-modal-close" onClick={closeAlphaRegimeModal}>&times;</button>
            </div>
            <div className="alpha-regime-modal-body">
              {isLoadingAlphaRegime ? (
                <div className="loading-spinner" style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : alphaRegimeData?.fund_series?.length > 0 ? (
                <>
                  <div className="alpha-regime-info">
                    <span>Asset Class: <strong>{alphaRegimeData.asset_class}</strong></span>
                    <span>Style: <strong>{alphaRegimeData.style || 'All'}</strong></span>
                    <span>Peer Count: <strong>{alphaRegimeData.peer_count}</strong></span>
                  </div>
                  <div className="alpha-regime-chart-container">
                    <Line
                      data={{
                        labels: alphaRegimeData.fund_series.map(p => p.date.substring(0, 7)),
                        datasets: [
                          {
                            label: alphaRegimeData.fund_name,
                            data: alphaRegimeData.fund_series.map(p => p.value),
                            borderColor: '#072040',
                            backgroundColor: 'transparent',
                            borderWidth: 2,
                            pointRadius: 0,
                            tension: 0.1,
                          },
                          {
                            label: `Peer Mean (${alphaRegimeData.peer_count} funds)`,
                            data: (() => {
                              const peerMap = new Map(alphaRegimeData.peer_series.map(p => [p.date, p.value]));
                              return alphaRegimeData.fund_series.map(p => peerMap.get(p.date) ?? null);
                            })(),
                            borderColor: '#6c757d',
                            backgroundColor: 'transparent',
                            borderWidth: 2,
                            borderDash: [5, 5],
                            pointRadius: 0,
                            tension: 0.1,
                          },
                          {
                            label: 'Relative (Fund - Peer)',
                            data: (() => {
                              const relMap = new Map(alphaRegimeData.relative_series.map(p => [p.date, p.value]));
                              return alphaRegimeData.fund_series.map(p => relMap.get(p.date) ?? null);
                            })(),
                            borderColor: 'transparent',
                            backgroundColor: alphaRegimeData.fund_series.map(p => {
                              const relMap = new Map(alphaRegimeData.relative_series.map(r => [r.date, r.value]));
                              const v = relMap.get(p.date);
                              return v != null && v >= 0 ? 'rgba(145, 207, 155, 0.3)' : 'rgba(231, 76, 60, 0.2)';
                            }),
                            fill: true,
                            pointRadius: 0,
                            tension: 0.1,
                          },
                        ]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { display: true, position: 'bottom', labels: { boxWidth: 12 } },
                          title: { display: true, text: 'Rolling 1yr Alpha: Fund vs Peer Group Mean', font: { size: 14, weight: 'bold' }, color: '#072040' },
                          tooltip: { mode: 'index', intersect: false, callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y?.toFixed(2)}%` } }
                        },
                        scales: {
                          x: { ticks: { maxTicksLimit: 12 } },
                          y: { ticks: { callback: v => `${v.toFixed(1)}%` } }
                        }
                      }}
                    />
                  </div>
                </>
              ) : (
                <p className="text-muted" style={{ textAlign: 'center', padding: '20px' }}>No data available</p>
              )}
            </div>
            <div className="alpha-regime-modal-footer">
              <button className="btn btn-secondary" onClick={closeAlphaRegimeModal}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Factor Loading Modal (single fund) */}
      {isDrummondUser && factorModalOpen && (
        <div className="factor-modal-overlay" onClick={closeFactorModal}>
          <div className="factor-modal" onClick={(e) => e.stopPropagation()}>
            <div className="factor-modal-header">
              <h5>Factor Loadings — {factorModalData?.request_name || ''}</h5>
              <button className="factor-modal-close" onClick={closeFactorModal}>
                &times;
              </button>
            </div>
            <div className="factor-modal-body">
              {isLoadingFactorModal ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : factorModalData?.found === false ? (
                <p className="text-muted" style={{ textAlign: 'center', padding: '30px' }}>
                  No factor loading data available for this fund.
                </p>
              ) : factorModalData?.fund ? (
                <>
                  {/* Summary bar */}
                  <div className="factor-modal-summary">
                    <div className="factor-summary-card" title="The equity sub-asset class used to determine which factor universe this fund is regressed against.">
                      <span className="factor-summary-label">Sub Asset Class</span>
                      <span className="factor-summary-value">{factorModalData.fund.sub_asset_class}</span>
                    </div>
                    <div className="factor-summary-card" title="The fund's investment style classification (e.g. Growth, Value, Blend). Used to construct the style factor portfolios.">
                      <span className="factor-summary-label">Style</span>
                      <span className="factor-summary-value">{factorModalData.fund.style || '-'}</span>
                    </div>
                    <div className="factor-summary-card" title="The fund's tilt classification (e.g. Quality, Momentum, Income). Used to construct the tilt factor portfolios.">
                      <span className="factor-summary-label">Tilt</span>
                      <span className="factor-summary-value">{factorModalData.fund.tilt || '-'}</span>
                    </div>
                    <div className="factor-summary-card" title="R-squared measures how much of the fund's return variation is explained by the factor model. 1.0 = perfectly explained, 0.0 = no explanatory power. Higher values mean the factor loadings are a reliable description of the fund.">
                      <span className="factor-summary-label">R²</span>
                      <span className="factor-summary-value">
                        {factorModalData.fund.r_squared != null ? factorModalData.fund.r_squared.toFixed(3) : '-'}
                      </span>
                    </div>
                    <div className="factor-summary-card" title="The number of monthly return observations used in the regression. More months gives a more stable estimate. Minimum 36 months required, up to 60 months used.">
                      <span className="factor-summary-label">N Months</span>
                      <span className="factor-summary-value">{factorModalData.fund.n_months ?? '-'}</span>
                    </div>
                    <div className="factor-summary-card" title="Monthly alpha-of-alpha — the average monthly return not explained by the fund's factor exposures. Positive means the fund generates excess return beyond what its style and tilt betas would predict (manager skill or other unexplained sources).">
                      <span className="factor-summary-label">Intercept</span>
                      <span className="factor-summary-value">
                        {factorModalData.fund.intercept != null ? (factorModalData.fund.intercept * 100).toFixed(2) + '%' : '-'}
                      </span>
                    </div>
                  </div>

                  {/* Risk decomposition */}
                  <div className="factor-risk-bar">
                    <div className="factor-risk-item" title="Annualised risk attributable to the fund's factor exposures. Calculated from the factor betas and the factor covariance matrix. This is the 'systematic' risk the fund takes through its style and tilt positioning.">
                      <span className="factor-risk-label">Factor Risk</span>
                      <span className="factor-risk-value">
                        {factorModalData.fund.factor_risk_annual != null
                          ? (factorModalData.fund.factor_risk_annual * 100).toFixed(2) + '%' : '-'}
                      </span>
                    </div>
                    <div className="factor-risk-item" title="Annualised idiosyncratic (specific) risk — the volatility of returns not explained by the factor model. High specific risk means the fund has meaningful return drivers outside of the modelled factors (stock selection, timing, etc).">
                      <span className="factor-risk-label">Specific Risk</span>
                      <span className="factor-risk-value">
                        {factorModalData.fund.specific_risk_annual != null
                          ? (factorModalData.fund.specific_risk_annual * 100).toFixed(2) + '%' : '-'}
                      </span>
                    </div>
                    <div className="factor-risk-item" title="Total annualised risk = sqrt(Factor Risk² + Specific Risk²). The overall volatility of the fund's returns, decomposed into factor-driven and fund-specific components.">
                      <span className="factor-risk-label">Total Risk</span>
                      <span className="factor-risk-value" style={{ fontWeight: 700 }}>
                        {factorModalData.fund.total_risk_annual != null
                          ? (factorModalData.fund.total_risk_annual * 100).toFixed(2) + '%' : '-'}
                      </span>
                    </div>
                  </div>

                  {/* Betas table */}
                  <div style={{ overflowX: 'auto' }}>
                    <table className="factor-table factor-modal-table">
                      <thead>
                        <tr>
                          <th title="The factor name. Style factors (S:) capture return differences between investment styles (e.g. Growth vs non-Growth). Tilt factors (T:) capture return differences between tilts (e.g. Quality vs non-Quality).">Factor</th>
                          <th title="The fund's sensitivity (loading) to this factor. 1.0 means the fund moves 1-for-1 with the factor. Higher absolute values indicate stronger exposure. Positive = aligned with the factor, negative = opposite exposure.">Beta</th>
                          {factorModalData.style_tilt_avg_betas && (
                            <th title="Average beta across all peer funds with the same Style">
                              Style Avg ({factorModalData.style_tilt_count || 0})
                            </th>
                          )}
                          <th title="Average beta across all funds in the same Sub Asset Class">
                            Sub Asset Class Avg ({factorModalData.sac_count || 0})
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(factorModalData.factors || Object.keys(factorModalData.fund.betas || {})).map(name => {
                          const val = factorModalData.fund.betas?.[name];
                          return (
                            <tr key={name}>
                            <td>{name.replace(/_/g, ' ')}</td>
                            <td
                              className="factor-beta-cell"
                              style={{ backgroundColor: getBetaColor(val) }}
                            >
                              {val != null ? val.toFixed(2) : '-'}
                            </td>
                            {factorModalData.style_tilt_avg_betas && (
                              <td
                                className="factor-beta-cell"
                                style={{ backgroundColor: getBetaColor(factorModalData.style_tilt_avg_betas[name]) }}
                              >
                                {factorModalData.style_tilt_avg_betas[name] != null
                                  ? factorModalData.style_tilt_avg_betas[name].toFixed(2) : '-'}
                              </td>
                            )}
                            <td
                              className="factor-beta-cell"
                              style={{ backgroundColor: getBetaColor(factorModalData.sac_avg_betas?.[name]) }}
                            >
                              {factorModalData.sac_avg_betas?.[name] != null
                                ? factorModalData.sac_avg_betas[name].toFixed(2) : '-'}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : null}
            </div>
            <div className="factor-modal-footer">
              <button className="btn btn-secondary" onClick={closeFactorModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Factor Risk Model Methodology Modal */}
      {isDrummondUser && factorMethodOpen && (
        <div className="factor-modal-overlay" onClick={() => setFactorMethodOpen(false)}>
          <div className="factor-method-modal" onClick={(e) => e.stopPropagation()}>
            <div className="factor-modal-header">
              <h5>Equity Factor Risk Model — Methodology</h5>
              <button className="factor-modal-close" onClick={() => setFactorMethodOpen(false)}>
                &times;
              </button>
            </div>
            <div className="factor-method-body">

              <section className="method-section">
                <h6>Purpose</h6>
                <p>The Equity Factor Risk Model assigns interpretable factor loadings to each equity managed fund. It answers questions such as:</p>
                <ul>
                  <li>"How much Growth exposure does this portfolio have?"</li>
                  <li>"What proportion of risk comes from style tilts versus stock selection?"</li>
                  <li>"How diversified is the portfolio across factor dimensions?"</li>
                </ul>
                <p>The model covers two equity factor groups: <strong>Australian Equities (EQ_AU)</strong> and <strong>International Equities (EQ_INTL)</strong>, where EQ_INTL pools International Developed (EQ_WORLD) and Emerging Market (EQ_EM) funds into a single factor universe.</p>
              </section>

              <section className="method-section">
                <h6>Approach: Hybrid Classification-PCA</h6>
                <p>The model uses a <strong>classification-based</strong> factor construction validated by <strong>Principal Component Analysis (PCA)</strong>. Classification factors are preferred over purely statistical (PCA) factors because they are directly interpretable — a "Growth factor" means something to portfolio managers, whereas "Principal Component 3" does not.</p>
                <p>PCA is run separately as a validation step to confirm that the classification-based factors capture the main dimensions of alpha variation across funds.</p>
              </section>

              <section className="method-section">
                <h6>Data Inputs</h6>
                <table className="method-table">
                  <thead><tr><th>Input</th><th>Description</th></tr></thead>
                  <tbody>
                    <tr><td>Cumulative alpha series</td><td>Monthly cumulative alpha for each fund vs its Drummond-assigned benchmark</td></tr>
                    <tr><td>Fund metadata</td><td>Style, Tilt, Sub Asset Class, Hedged status, Held flag, APL status per fund</td></tr>
                    <tr><td>Tracking error</td><td>From the AP List table — used to construct the HighConviction factor</td></tr>
                  </tbody>
                </table>
                <p><strong>Alpha</strong> is defined as the simple return difference between a fund and its Drummond-assigned benchmark, compounded cumulatively over time. Drummond assigns a representative passive fund or ETF for each sub-asset class as the benchmark — this may differ from the fund's own stated benchmark. This ensures consistent, like-for-like comparison across all funds within the same sub-asset class.</p>
              </section>

              <section className="method-section">
                <h6>Step 1: Monthly Alpha Derivation</h6>
                <p>Cumulative alpha is converted to monthly alpha returns:</p>
                <code className="method-formula">{'monthly_alpha_t = (1 + cumulative_alpha_t) / (1 + cumulative_alpha_{t-1}) - 1'}</code>
                <p>The model uses a <strong>60-month (5-year) estimation window</strong>. Funds with fewer than <strong>36 months</strong> of data are excluded. For EQ_INTL, monthly alpha is derived separately for EQ_WORLD and EQ_EM funds then concatenated into a single matrix.</p>
              </section>

              <section className="method-section">
                <h6>Step 2: Factor Construction</h6>
                <p>Factors are built from three sources:</p>
                <ul>
                  <li><strong>Style</strong> — the fund's broad investment approach (e.g. Growth, Value, Neutral, Income)</li>
                  <li><strong>Tilt</strong> — a secondary characteristic (e.g. Quality, Quant, Smallcap, Midcap, Microcap)</li>
                  <li><strong>HighConviction</strong> — funds in the top quartile of tracking error within each factor group, capturing the return premium (or penalty) from high-conviction active management. Requires at least 20 funds with tracking error data.</li>
                </ul>
                <p>For each factor group, the model computes the <strong>equal-weighted average monthly alpha</strong> of all funds sharing the same classification. A minimum of <strong>5 funds</strong> is required to form a factor.</p>
                <p>Style and Tilt operate as <strong>independent dimensions</strong>. A Growth/Quality fund contributes to both Style_Growth and Tilt_Quality. The regression then determines how much alpha is attributable to each.</p>
                <p><strong>International pooling:</strong> EQ_WORLD and EQ_EM funds are pooled into a single <strong>EQ_INTL</strong> factor group. This provides a larger, more robust factor universe for international equities.</p>
                <p><strong>Per-group exclusions:</strong> Certain styles and tilts are excluded on a per-group basis because they represent structurally different return profiles or are redundant with other factors:</p>
                <table className="method-table">
                  <thead><tr><th>Group</th><th>Excluded Styles</th><th>Excluded Tilts</th></tr></thead>
                  <tbody>
                    <tr><td>EQ_AU</td><td>Geared, Long Short, Sustainable, SMID</td><td>Growth, Neutral, Positive Screening, Value, Negative Screening</td></tr>
                    <tr><td>EQ_INTL</td><td>Long Short, Sustainable, Sectoral</td><td>Negative Screening, Positive Screening, Value, Asia</td></tr>
                  </tbody>
                </table>
              </section>

              <section className="method-section">
                <h6>Step 3: PCA Validation</h6>
                <p>PCA is run on the fund alpha matrix to extract principal components. The top PCs are correlated with the classification-based factors to confirm they capture the main dimensions. If a PC explains {'>'}5% of variance but has no meaningful correlation (|r| {'<'} 0.4) with any classification factor, it is flagged as a potential missing factor dimension.</p>
              </section>

              <section className="method-section">
                <h6>Step 4: Multicollinearity Check</h6>
                <p>The full factor correlation matrix is computed. If any pair of factors has a correlation above <strong>0.85</strong>, one is dropped — keeping the factor with more constituent funds.</p>
              </section>

              <section className="method-section">
                <h6>Step 5: Factor Loading Estimation (OLS)</h6>
                <code className="method-formula">fund_monthly_alpha = intercept + β₁ × Factor₁ + β₂ × Factor₂ + ... + ε</code>
                <p>Each fund is regressed using <strong>ordinary least squares (OLS)</strong> only against the factors from its own factor group. The outputs per fund are:</p>
                <table className="method-table">
                  <thead><tr><th>Output</th><th>Description</th></tr></thead>
                  <tbody>
                    <tr><td><strong>Betas</strong></td><td>Loading on each factor (shown as %) — how much the fund's alpha moves with each style/tilt factor</td></tr>
                    <tr><td><strong>Intercept</strong></td><td>Alpha-of-alpha — residual return not explained by any factor (stock selection skill)</td></tr>
                    <tr><td><strong>R-squared</strong></td><td>Proportion of alpha variance explained by the factors</td></tr>
                    <tr><td><strong>Specific risk</strong></td><td>Annualised standard deviation of the regression residuals (idiosyncratic risk)</td></tr>
                    <tr><td><strong>Factor risk</strong></td><td>Annualised risk attributable to factor exposures</td></tr>
                    <tr><td><strong>Total risk</strong></td><td>Annualised standard deviation of the fund's monthly alpha</td></tr>
                  </tbody>
                </table>
              </section>

              <section className="method-section">
                <h6>Step 6: Risk Model Assembly</h6>
                <code className="method-formula">Total Variance = Factor Variance + Specific Variance{'\n'}             = w′ B′ Σ_f B w  +  Σ(wᵢ² × σ_specificᵢ²)</code>
                <p>This separates portfolio risk into:</p>
                <ul>
                  <li><strong>Factor risk</strong> — risk from exposure to systematic style/tilt factors (diversifiable by mixing styles)</li>
                  <li><strong>Specific risk</strong> — idiosyncratic risk from individual fund manager stock selection (diversifiable by adding more funds)</li>
                </ul>
              </section>

              <section className="method-section">
                <h6>Interpreting Factor Loadings (Betas)</h6>
                <p>Betas are displayed as percentages. A beta of <strong>+100%</strong> on Style_Growth means the fund's monthly alpha moves one-for-one with the average alpha of all Growth-style funds. A beta of <strong>0%</strong> means no relationship. Negative betas indicate the fund behaves oppositely to that factor.</p>
                <p><strong>Index/passive funds</strong> will typically have betas near zero — their alpha is close to zero by construction.</p>
                <p><em>Caveat:</em> Funds benchmarked against a broad index rather than their specific benchmark (e.g. a Japan ETF benchmarked against a global equity index) may show misleading factor loadings.</p>
              </section>

              <section className="method-section">
                <h6>Interpreting R-squared</h6>
                <ul>
                  <li><strong>R² {'>'} 0.6:</strong> Strong fit — the fund behaves consistently with its style/tilt peers</li>
                  <li><strong>R² 0.3–0.6:</strong> Moderate fit — some idiosyncratic behaviour</li>
                  <li><strong>R² {'<'} 0.3:</strong> Weak fit — highly idiosyncratic or potentially misclassified</li>
                </ul>
              </section>

              <section className="method-section">
                <h6>Interpreting the Intercept</h6>
                <p>The regression intercept represents the portion of a fund's average monthly alpha not explained by factor exposures. A positive intercept suggests the fund generates return above what its style/tilt exposures would predict — potentially indicating genuine stock selection skill. The mean intercept across all funds is near zero, confirming no systematic bias.</p>
              </section>

              <section className="method-section">
                <h6>Risk Decomposition</h6>
                <ul>
                  <li><strong>High factor share ({'>'}60%):</strong> Portfolio risk is dominated by style bets — diversify across styles to reduce risk</li>
                  <li><strong>High specific share ({'>'}60%):</strong> Portfolio risk is dominated by individual fund selection — the style mix is diversified but individual fund choices are driving risk</li>
                </ul>
              </section>

              <section className="method-section">
                <h6>Model Parameters</h6>
                <table className="method-table">
                  <thead><tr><th>Parameter</th><th>Value</th><th>Rationale</th></tr></thead>
                  <tbody>
                    <tr><td>Estimation window</td><td>60 months</td><td>Long enough for stable estimates, short enough to adapt to regime changes</td></tr>
                    <tr><td>Min fund history</td><td>36 months</td><td>Ensures regression has adequate degrees of freedom</td></tr>
                    <tr><td>Min funds per factor</td><td>5</td><td>Smaller groups produce noisy factor estimates</td></tr>
                    <tr><td>Min funds for HighConviction</td><td>20 (with TE data)</td><td>Needs enough funds to compute a meaningful top quartile</td></tr>
                    <tr><td>Multicollinearity threshold</td><td>0.85</td><td>Drops near-duplicate factors</td></tr>
                    <tr><td>Excluded styles (global)</td><td>Passive, Benchmark</td><td>Near-zero alpha by design</td></tr>
                    <tr><td>Excluded tilts (global)</td><td>Passive, Benchmark, None</td><td>"None" is not a meaningful grouping</td></tr>
                  </tbody>
                </table>
              </section>

              <section className="method-section">
                <h6>Limitations</h6>
                <ol>
                  <li><strong>Benchmark assignment matters:</strong> All alpha-derived metrics (tracking error, alpha, factor loadings) use a Drummond-assigned representative passive fund/ETF per sub-asset class, not the fund's stated benchmark. Funds whose true exposure differs from their sub-asset class assignment may show misleading factor loadings.</li>
                  <li><strong>Equal-weighted factors:</strong> Factor returns are equal-weighted averages — larger funds are not given greater influence.</li>
                  <li><strong>No cross-factor-group factors:</strong> EQ_AU and EQ_INTL have independent factors. Portfolio risk across factor groups assumes independence.</li>
                  <li><strong>Static classification:</strong> Style and Tilt labels are from the current metadata snapshot — historical assignments do not update retrospectively.</li>
                  <li><strong>Estimation window sensitivity:</strong> The 60-month window reflects the most recent 5 years only.</li>
                  <li><strong>EQ_INTL pooling:</strong> Pooling EQ_WORLD and EQ_EM means emerging market funds are regressed against factors that include developed market peers. This is intentional but may reduce fit for funds with purely EM-specific alpha drivers.</li>
                </ol>
              </section>

            </div>
            <div className="factor-modal-footer">
              <button className="btn btn-secondary" onClick={() => setFactorMethodOpen(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Research Report Modals */}
      {openReportModals.map(apir => (
        <ResearchReportModal
          key={apir}
          isOpen={true}
          onClose={() => setOpenReportModals(prev => prev.filter(a => a !== apir))}
          apirCode={apir}
        />
      ))}

      {/* Disclaimer Card */}
      <div className="card disclaimer-card mt-3">
        <div className="card-body">
          <div className="disclaimer-text">
            <ManagedDisclaimer dkey="data_notice_fund_screener" as="p" className="small text-muted mb-0" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FundScreenerApp;
